import {
  pgTable,
  pgView,
  text,
  varchar,
  timestamp,
  jsonb,
  index,
  uniqueIndex,
  serial,
  boolean,
  integer,
  date,
  check,
  numeric,
  doublePrecision,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
import { createSchemaFactory } from "drizzle-zod";
import { z } from "zod";

// Initialize schema factory with Zod instance for consistent type identity
const { createInsertSchema, createSelectSchema } = createSchemaFactory({
  zodInstance: z,
});

// Session storage table (mandatory for Replit Auth)
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)]
);

// User storage table
export const users = pgTable(
  "users",
  {
    id: serial("id").primaryKey(),
    username: varchar("username", { length: 50 }).unique().notNull(),
    email: varchar("email").unique().notNull(),
    password: varchar("password").notNull(),
    firstName: varchar("first_name"),
    lastName: varchar("last_name"),
    profileImageUrl: varchar("profile_image_url"),
    phone: varchar("phone"),
    dateOfBirth: date("dateOfBirth"),
    gender: varchar("gender"),
    location: varchar("location"),
    interests: text("interests"),
    socialHandles: jsonb("socialHandles"),
    consentGiven: boolean("consentGiven").default(false),
    marketingConsent: boolean("marketingConsent").default(false),
    termsAccepted: boolean("termsAccepted").default(false),
    platform: varchar("platform", { length: 20 }).notNull().default("web"),
    ageVerified: boolean("ageVerified").default(false),
    isBanned: boolean("isBanned").default(false),
    isActive: boolean("isActive").default(true), // For user activation/deactivation
    userStatus: varchar("user_status", { length: 30 }).default("active"), // active, email_unverified, banned, inactive
    referralCode: varchar("referralCode", { length: 20 }).unique(), // Unique referral code
    referredBy: integer("referredBy"), // ID of user who referred this user
    gamifiedName: varchar("gamified_name", { length: 50 }).unique(), // For challenges system
    shareTier: varchar("share_tier", { length: 20 }).default("bronze"), // Share verification tier progression
    tierPoints: integer("tier_points").default(0), // Points for tier advancement
    profileCompletion: integer("profile_completion").default(0), // Profile completion percentage (0-100)
    // Progressive Profiling extensions
    currentTier: varchar("current_tier", { length: 20 }).default("basic"), // basic, intermediate, advanced, complete
    profileConsentGiven: boolean("profile_consent_given").default(false), // Consent for progressive profiling
    lastProfileUpdate: timestamp("last_profile_update").defaultNow(),
    segmentId: integer("segment_id"), // Reference to user segment assignment
    segmentScore: numeric("segment_score").default("0"), // Confidence score for segment assignment
    profileMetadata: jsonb("profile_metadata"), // Flexible storage for profile insights
    // Login tracking fields
    lastLoginIp: varchar("last_login_ip"), // User's current/last IP address
    lastLoginLocation: varchar("last_login_location"), // Location derived from IP geolocation
    lastLoginTimestamp: timestamp("last_login_timestamp"), // When user last logged in
    loginHistory: jsonb("login_history"), // Array of recent login records: [{ timestamp, ip, location, device, browser, os, userAgent }]
    // Enhanced device and browser tracking fields
    lastLoginDevice: varchar("last_login_device"), // Device type: desktop, mobile, tablet
    lastLoginBrowser: varchar("last_login_browser"), // Browser name and version (e.g., "Chrome 118.0")
    lastLoginOS: varchar("last_login_os"), // Operating system (e.g., "Windows 11", "iOS 17.1", "Android 14")
    lastLoginUserAgent: text("last_login_user_agent"), // Full user agent string for detailed analysis
    welcomeEmailSent: boolean("welcome_email_sent").default(false), // Has the welcome email been sent directly
    welcomeEmailSentAt: timestamp("welcome_email_sent_at"),
    // ID Verification fields for fraud protection
    idNumber: varchar("id_number"), // South African ID number or passport number
    idDocumentType: varchar("id_document_type", { length: 20 }), // 'id_number' or 'passport'
    idVerified: boolean("id_verified").default(false), // Has the ID been verified
    idVerifiedAt: timestamp("id_verified_at"), // When ID was verified
    // Phone Verification fields for SMS OTP
    phoneVerified: boolean("phone_verified").default(false), // Has the phone been verified
    phoneVerifiedAt: timestamp("phone_verified_at"), // When phone was verified
    // Username customization tracking
    hasCustomizedUsername: boolean("has_customized_username").default(false), // Has user customized their auto-generated username
    // Registration source tracking
    source: varchar("source", { length: 50 }), // Registration source (e.g., "bulk_import", "facebook_lead_ad", "web")
    createdAt: timestamp("createdAt").defaultNow(),
    updatedAt: timestamp("updatedAt").defaultNow(),
  },
  (table) => [
    index("idx_users_created_at").on(table.createdAt),
    index("idx_users_referred_by").on(table.referredBy),
    index("idx_users_is_active").on(table.isActive),
    index("idx_users_share_tier").on(table.shareTier),
    index("idx_users_tier_points").on(table.tierPoints),
    index("idx_users_current_tier").on(table.currentTier),
    index("idx_users_segment_id").on(table.segmentId),
    index("idx_users_profile_completion").on(table.profileCompletion),
    index("idx_users_user_status").on(table.userStatus),
    // Indexes for enhanced login tracking fields
    index("idx_users_last_login_timestamp").on(table.lastLoginTimestamp),
    index("idx_users_last_login_device").on(table.lastLoginDevice),
    index("idx_users_last_login_os").on(table.lastLoginOS),
    // Indexes for ID verification fields
    index("idx_users_id_verified").on(table.idVerified),
    index("idx_users_id_document_type").on(table.idDocumentType),
    // Index for phone verification
    index("idx_users_phone_verified").on(table.phoneVerified),
    index("idx_users_platform").on(table.platform),
  ]
);

// Admin users table
export const admins = pgTable("admins", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id),
  role: varchar("role").notNull().default("admin"), // admin, superadmin
  twoFactorSecret: varchar("two_factor_secret"), // TOTP secret for 2FA
  twoFactorEnabled: boolean("two_factor_enabled").default(false),
  twoFactorVerified: boolean("two_factor_verified").default(false), // Must be true for admin access
  backupCodes: jsonb("backup_codes"), // Array of backup codes for 2FA recovery
  lastTwoFactorVerification: timestamp("last_two_factor_verification"),
  twoFactorSessionId: varchar("two_factor_session_id"), // Track browser session for 2FA
  createdAt: timestamp("created_at").defaultNow(),
});

// OTP Verifications table for SMS verification
export const otpVerifications = pgTable(
  "otp_verifications",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id)
      .notNull(),
    phone: varchar("phone").notNull(),
    otp: varchar("otp", { length: 6 }).notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    verified: boolean("verified").default(false),
    attempts: integer("attempts").default(0),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_otp_user_id").on(table.userId),
    index("idx_otp_phone").on(table.phone),
    index("idx_otp_expires_at").on(table.expiresAt),
    index("idx_otp_verified").on(table.verified),
  ]
);

// Competitions table
export const competitions = pgTable(
  "competitions",
  {
    id: serial("id").primaryKey(),
    title: varchar("title").notNull(),
    description: text("description"),
    prize: varchar("prize"), // Legacy prize field (kept for database compatibility)
    prize1: varchar("prize1"), // First prize
    prize2: varchar("prize2"), // Second prize
    prize3: varchar("prize3"), // Third prize
    prizeValue: integer("prizeValue"),
    startDate: timestamp("startDate", { withTimezone: true }).notNull(), // Full date and time for scheduling
    startTime: varchar("startTime"), // Time in HH:MM format for UI display
    endDate: timestamp("endDate", { withTimezone: true }), // Full date and time with timezone
    endTime: varchar("endTime"), // Time in HH:MM format for UI display
    type: varchar("type").notNull(), // daily, weekly, monthly
    category: varchar("category").notNull(),
    daysOfWeek: text("daysOfWeek").array(), // Array of days when competition is active: ['monday', 'tuesday', etc.]
    bannerImageUrl: varchar("bannerImageUrl"),
    shareText: text("shareText"),
    termsAndConditionsDialog: text("terms_and_conditions_dialog"), // Optional custom T&Cs dialog text for specific competition
    betfredEnabled: boolean("betfredEnabled").default(true), // Enable Betfred signup for this specific competition
    lastResetDate: date("lastResetDate"), // Track when daily competition was last reset
    dailyStartHour: integer("dailyStartHour").default(7), // Hour when daily competition starts (0-23)
    isRecurring: boolean("isRecurring").default(false), // For daily competitions that auto-renew
    requiredHashtag: varchar("required_hashtag", { length: 100 }), // Required hashtag for share verification
    shareRules: jsonb("share_rules"), // Share verification rules and bonus multipliers
    facebookFormId: varchar("facebook_form_id"), // Facebook Lead Form ID for this competition
    facebookPageId: varchar("facebook_page_id"), // Facebook Page ID used for the lead form
    facebookFormUrl: varchar("facebook_form_url"), // URL to the Facebook form
    status: varchar("status", { length: 20 }).notNull().default("draft"), // 'draft', 'active', 'ended'
    isActive: boolean("isActive").default(true), // Whether competition is currently active
    createdBy: integer("createdBy").references(() => users.id),
    createdAt: timestamp("createdAt").defaultNow(),
    updatedAt: timestamp("updatedAt").defaultNow(),
  },
  (table) => [
    index("idx_competitions_is_active").on(table.isActive),
    index("idx_competitions_type").on(table.type),
    index("idx_competitions_category").on(table.category),
    index("idx_competitions_start_date").on(table.startDate),
    index("idx_competitions_end_date").on(table.endDate),
    index("idx_competitions_created_by").on(table.createdBy),
    index("idx_competitions_created_at").on(table.createdAt),
  ]
);

// Daily Reset Log table to track reset operations
export const dailyResetLog = pgTable("daily_reset_log", {
  id: serial("id").primaryKey(),
  resetDate: date("reset_date").notNull(),
  competitionsReset: integer("competitions_reset").notNull().default(0),
  entriesCleared: integer("entries_cleared").notNull().default(0),
  answersCleared: integer("answers_cleared").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

// Global Questions table - standalone question pool
export const globalQuestions = pgTable("global_questions", {
  id: serial("id").primaryKey(),
  questionText: text("question_text").notNull(),
  questionType: varchar("question_type").notNull(), // mcq, open, true_false
  options: jsonb("options"), // array for MCQ options
  correctAnswer: varchar("correct_answer"),
  category: varchar("category").notNull().default("general"),
  difficulty: varchar("difficulty").notNull().default("easy"), // easy, medium, hard
  isActive: boolean("is_active").default(true),
  createdBy: integer("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Brand Questions table - vendor/brand specific questions
export const brandQuestions = pgTable("brand_questions", {
  id: serial("id").primaryKey(),
  questionText: text("question_text").notNull(),
  questionType: varchar("question_type").notNull(), // mcq, open, true_false
  options: jsonb("options"), // array for MCQ options
  correctAnswer: varchar("correct_answer"),
  category: varchar("category").notNull().default("brand"),
  difficulty: varchar("difficulty").notNull().default("easy"),
  vendorId: integer("vendor_id").references(() => vendors.id),
  isActive: boolean("is_active").default(true),
  createdBy: integer("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Behavioral Questions table - user behavior/preference questions
export const behavioralQuestions = pgTable("behavioral_questions", {
  id: serial("id").primaryKey(),
  questionText: text("question_text").notNull(),
  questionType: varchar("question_type").notNull(), // mcq, open, true_false
  options: jsonb("options"), // array for MCQ options
  correctAnswer: varchar("correct_answer"),
  category: varchar("category").notNull().default("behavioral"),
  difficulty: varchar("difficulty").notNull().default("easy"),
  isActive: boolean("is_active").default(true),
  createdBy: integer("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ============================================================================
// PROGRESSIVE PROFILING SYSTEM TABLES
// ============================================================================

// Profile Questions table - Dynamic questionnaires for progressive profiling
export const profileQuestions = pgTable(
  "profile_questions",
  {
    id: serial("id").primaryKey(),
    questionText: text("question_text").notNull(),
    questionType: varchar("question_type").notNull(), // mcq, open, scale, boolean, multi_select
    options: jsonb("options"), // Array of options for MCQ/multi-select
    category: varchar("category").notNull(), // demographics, behavioral, psychographic, preferences
    subcategory: varchar("subcategory"), // age, location, shopping_habits, lifestyle, etc.
    tier: varchar("tier").notNull(), // basic, intermediate, advanced, complete
    priority: integer("priority").default(0), // Lower number = higher priority
    isRequired: boolean("is_required").default(false),
    skipLogic: jsonb("skip_logic"), // Rules for when to skip this question
    validationRules: jsonb("validation_rules"), // Frontend and backend validation
    personalityInsight: varchar("personality_insight"), // Which personality trait this question reveals
    segmentWeight: numeric("segment_weight").default("1.0"), // Weight for segmentation algorithm
    businessValue: varchar("business_value"), // high, medium, low - importance to business
    isActive: boolean("is_active").default(true),
    createdBy: integer("created_by").references(() => users.id),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_profile_questions_tier").on(table.tier),
    index("idx_profile_questions_category").on(table.category),
    index("idx_profile_questions_priority").on(table.priority),
    index("idx_profile_questions_is_active").on(table.isActive),
  ]
);

// Profile Answers table - User responses to profile questions
export const profileAnswers = pgTable(
  "profile_answers",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    questionId: integer("question_id")
      .references(() => profileQuestions.id, { onDelete: "cascade" })
      .notNull(),
    answerValue: text("answer_value"), // Actual answer content
    selectedOptions: jsonb("selected_options"), // For MCQ/multi-select questions
    confidenceScore: numeric("confidence_score").default("1.0"), // User confidence in their answer
    responseTime: integer("response_time"), // Time taken to answer in seconds
    sessionId: varchar("session_id"), // Grouping answers by session
    isSkipped: boolean("is_skipped").default(false),
    skipReason: varchar("skip_reason"), // why_skipped: not_applicable, too_personal, etc.
    metadata: jsonb("metadata"), // Additional tracking data
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_profile_answers_user_id").on(table.userId),
    index("idx_profile_answers_question_id").on(table.questionId),
    index("idx_profile_answers_session_id").on(table.sessionId),
    index("idx_profile_answers_created_at").on(table.createdAt),
    // Composite index for finding user's answers to specific questions
    index("idx_profile_answers_user_question").on(
      table.userId,
      table.questionId
    ),
  ]
);

// Profile Sessions table - Track user profiling sessions
export const profileSessions = pgTable(
  "profile_sessions",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    sessionId: varchar("session_id").notNull(),
    tier: varchar("tier").notNull(), // Which tier they were completing
    startedAt: timestamp("started_at").defaultNow(),
    completedAt: timestamp("completed_at"),
    questionsShown: integer("questions_shown").default(0),
    questionsAnswered: integer("questions_answered").default(0),
    questionsSkipped: integer("questions_skipped").default(0),
    totalTimeSpent: integer("total_time_spent").default(0), // in seconds
    completionRate: numeric("completion_rate").default("0"), // percentage
    abandonedAt: timestamp("abandoned_at"), // If session was abandoned
    abandonReason: varchar("abandon_reason"), // too_long, technical_issue, privacy_concern
    deviceType: varchar("device_type"), // mobile, desktop, tablet
    userAgent: text("user_agent"),
    ipAddress: varchar("ip_address"), // For analytics, anonymized after 30 days
    isCompleted: boolean("is_completed").default(false),
    metadata: jsonb("metadata"),
  },
  (table) => [
    index("idx_profile_sessions_user_id").on(table.userId),
    index("idx_profile_sessions_session_id").on(table.sessionId),
    index("idx_profile_sessions_tier").on(table.tier),
    index("idx_profile_sessions_completed").on(table.isCompleted),
    index("idx_profile_sessions_started_at").on(table.startedAt),
  ]
);

// User Tier Progress table - Track progress through profiling tiers
export const userTierProgress = pgTable(
  "user_tier_progress",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    tier: varchar("tier").notNull(),
    totalQuestions: integer("total_questions").default(0),
    answeredQuestions: integer("answered_questions").default(0),
    skippedQuestions: integer("skipped_questions").default(0),
    completionPercentage: numeric("completion_percentage").default("0"),
    isCompleted: boolean("is_completed").default(false),
    completedAt: timestamp("completed_at"),
    lastQuestionAt: timestamp("last_question_at"),
    estimatedTimeRemaining: integer("estimated_time_remaining"), // in minutes
    achievements: jsonb("achievements"), // Badges/rewards earned
    tier_score: numeric("tier_score").default("0"), // Quality score for this tier's responses
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_user_tier_progress_user_id").on(table.userId),
    index("idx_user_tier_progress_tier").on(table.tier),
    index("idx_user_tier_progress_completed").on(table.isCompleted),
    // Ensure unique user-tier combinations
    index("idx_user_tier_progress_unique").on(table.userId, table.tier),
  ]
);

// Questions table - questions assigned to competitions
export const questions = pgTable("questions", {
  id: serial("id").primaryKey(),
  competitionId: integer("competitionId").references(() => competitions.id, {
    onDelete: "cascade",
  }),
  questionText: text("questionText").notNull(),
  questionType: varchar("questionType").notNull(), // mcq, open
  options: jsonb("options"), // array for MCQ options
  correctAnswer: varchar("correctAnswer"),
  isRequired: boolean("isRequired").default(true),
  order: integer("order").default(0),
  isActive: boolean("isActive").default(true),
  globalQuestionId: integer("globalQuestionId").references(
    () => globalQuestions.id,
    { onDelete: "set null" }
  ),
  brandQuestionId: integer("brandQuestionId").references(
    () => brandQuestions.id,
    { onDelete: "set null" }
  ),
  behavioralQuestionId: integer("behavioralQuestionId").references(
    () => behavioralQuestions.id,
    { onDelete: "set null" }
  ),
  category: varchar("category"),
  subCategory: varchar("sub_category"),
  keywords: text("keywords").array(),
  segmentTags: text("segment_tags").array(), // Explicit segment mapping tags
  brand: varchar("brand"),
  difficulty: varchar("difficulty"),
  createdAt: timestamp("createdAt").defaultNow(),
});

// Answers table
export const answers = pgTable(
  "answers",
  {
    id: serial("id").primaryKey(),
    questionId: integer("questionId").references(() => questions.id, {
      onDelete: "cascade",
    }),
    userId: integer("userId").references(() => users.id, {
      onDelete: "cascade",
    }),
    competitionId: integer("competitionId").references(() => competitions.id, {
      onDelete: "cascade",
    }),
    answerText: text("answerText").notNull(),
    createdAt: timestamp("createdAt").defaultNow(),
  },
  (table) => [
    index("idx_answers_user_id").on(table.userId),
    index("idx_answers_competition_id").on(table.competitionId),
    index("idx_answers_question_id").on(table.questionId),
    index("idx_answers_created_at").on(table.createdAt),
  ]
);

export const answerInsights = pgTable(
  "answer_insights",
  {
    id: serial("id").primaryKey(),
    questionId: integer("question_id")
      .references(() => questions.id, { onDelete: "cascade" })
      .notNull(),
    answerText: text("answer_text").notNull(),
    signal: varchar("signal", { length: 20 }).notNull().default("positive"),
    weight: numeric("weight", { precision: 3, scale: 2 }).default("1.0"),
    insightText: text("insight_text"),
    derivedTrait: text("derived_trait"),
    traitCategory: text("trait_category"),
    profilingUse: text("profiling_use"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_answer_insights_question").on(table.questionId),
    index("idx_answer_insights_signal").on(table.signal),
    unique("uniq_answer_insights_question_answer").on(
      table.questionId,
      table.answerText
    ),
  ]
);

export const traitSegmentMappings = pgTable(
  "trait_segment_mappings",
  {
    id: serial("id").primaryKey(),
    trait: text("trait").notNull(),
    segmentId: integer("segment_id")
      .references(() => userSegments.id, { onDelete: "cascade" })
      .notNull(),
    weight: numeric("weight", { precision: 3, scale: 2 }).default("1.0"),
    description: text("description"),
    aiGenerated: boolean("ai_generated").default(true),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_trait_segment_mappings_trait").on(table.trait),
    index("idx_trait_segment_mappings_segment").on(table.segmentId),
    unique("uniq_trait_segment_mappings").on(table.trait, table.segmentId),
  ]
);

// Entries table
export const entries = pgTable(
  "entries",
  {
    id: serial("id").primaryKey(),
    userId: integer("userId").references(() => users.id, {
      onDelete: "cascade",
    }),
    competitionId: integer("competitionId").references(() => competitions.id, {
      onDelete: "cascade",
    }),
    entryType: varchar("entryType").notNull(), // answer, share, referral, facebook_lead
    platform: varchar("platform"), // for shares: facebook, twitter, whatsapp, instagram
    referralId: integer("referralId"), // Reference to referral if entry is from referral
    metadata: jsonb("metadata"), // Additional entry metadata (source, ad info, etc.)
    createdAt: timestamp("createdAt").defaultNow(),
  },
  (table) => [
    index("idx_entries_user_id").on(table.userId),
    index("idx_entries_competition_id").on(table.competitionId),
    index("idx_entries_entry_type").on(table.entryType),
    index("idx_entries_platform").on(table.platform),
    index("idx_entries_created_at").on(table.createdAt),
    index("idx_entries_user_competition").on(table.userId, table.competitionId), // Composite index for common queries
  ]
);

// Social shares tracking table
export const socialShares = pgTable(
  "social_shares",
  {
    id: serial("id").primaryKey(),
    userId: integer("userId")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    competitionId: integer("competitionId")
      .references(() => competitions.id, { onDelete: "cascade" })
      .notNull(),
    platform: varchar("platform").notNull(), // facebook, whatsapp, twitter, instagram
    shareUrl: text("shareUrl"),
    shareDate: date("shareDate").notNull(),
    shareType: varchar("shareType").notNull().default("regular"), // regular, referral
    entryId: integer("entryId").references(() => entries.id, {
      onDelete: "set null",
    }),
    // Share Verification System fields
    status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, verified, failed, rejected
    hashtagUsed: varchar("hashtag_used", { length: 100 }), // Actual hashtag used in the post
    externalPostId: varchar("external_post_id", { length: 255 }), // Platform-specific post ID
    verifiedAt: timestamp("verified_at"), // When the share was verified
    metadata: jsonb("metadata"), // Additional verification metadata
    errorReason: text("error_reason"), // Reason for verification failure
    multiplierApplied: numeric("multiplier_applied", {
      precision: 3,
      scale: 2,
    }).default("1.00"), // Entry multiplier (1.00 = 1x, 2.00 = 2x, etc.)
    tierAtShare: varchar("tier_at_share", { length: 20 }), // User's tier when the share was made
    attemptCount: integer("attempt_count").default(0), // Number of verification attempts
    createdAt: timestamp("createdAt").defaultNow(),
  },
  (table) => [
    index("idx_social_shares_user_id").on(table.userId),
    index("idx_social_shares_competition_id").on(table.competitionId),
    index("idx_social_shares_platform").on(table.platform),
    index("idx_social_shares_share_date").on(table.shareDate),
    index("idx_social_shares_status").on(table.status),
    index("idx_social_shares_verified_at").on(table.verifiedAt),
    index("idx_social_shares_external_post_id").on(table.externalPostId),
  ]
);

// Referrals tracking table
export const referrals = pgTable(
  "referrals",
  {
    id: serial("id").primaryKey(),
    referrerId: integer("referrerId")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    referredUserId: integer("referredUserId").references(() => users.id, {
      onDelete: "cascade",
    }), // Nullable for pending referrals
    competitionId: integer("competitionId").references(() => competitions.id, {
      onDelete: "cascade",
    }),
    referralCode: varchar("referralCode", { length: 20 }).notNull(),
    referredEmail: varchar("referred_email", { length: 255 }), // Email of referred user (for pre-signup tracking)
    status: varchar("status").notNull().default("pending"), // pending, completed
    referrerEntryId: integer("referrerEntryId").references(() => entries.id, {
      onDelete: "set null",
    }),
    referredEntryId: integer("referredEntryId").references(() => entries.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("createdAt").defaultNow(),
    completedAt: timestamp("completedAt"),
  },
  (table) => [
    index("idx_referrals_referrer_id").on(table.referrerId),
    index("idx_referrals_referred_user_id").on(table.referredUserId),
    index("idx_referrals_referral_code").on(table.referralCode),
    index("idx_referrals_status").on(table.status),
    index("idx_referrals_competition_id").on(table.competitionId),
    index("idx_referrals_referred_email").on(table.referredEmail),
    // SECURITY CONSTRAINTS: Prevent duplicate referrals and gaming attempts
    unique("uniq_referrals_competition_user").on(
      table.competitionId,
      table.referredUserId
    ),
  ]
);

// Betfred signups table for tracking bonus entries (supports both competitions and challenges)
export const betfredSignups = pgTable(
  "betfred_signups",
  {
    id: serial("id").primaryKey(),
    userId: integer("userId")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    competitionId: integer("competitionId").references(() => competitions.id, {
      onDelete: "cascade",
    }), // For competition signups
    challengeId: integer("challengeId").references(() => challenges.id, {
      onDelete: "cascade",
    }), // For challenge signups
    betfredEmail: varchar("betfredEmail").notNull(), // Email used for Betfred signup
    signupDate: timestamp("signupDate").defaultNow(),
    entriesAwarded: integer("entriesAwarded").notNull().default(50), // Default 50 entries (or 2 for challenges)
    verified: boolean("verified").default(false), // Admin can verify the signup
    verificationScheduledAt: timestamp("verificationScheduledAt"), // When verification should happen (5 mins after signup)
    verificationAttempts: integer("verificationAttempts").default(0),
    lastVerificationAttempt: timestamp("lastVerificationAttempt"),
    apiResponse: jsonb("apiResponse"), // Store Betfred API response data
    registered: boolean("registered"), // Whether email is registered with Betfred
    registrationDate: timestamp("registrationDate"), // From Betfred API
    lastBetDate: timestamp("lastBetDate"), // From Betfred API
    lastDepositDate: timestamp("lastDepositDate"), // From Betfred API
    lastLoginDate: timestamp("lastLoginDate"), // From Betfred API
    verificationStatus: varchar("verificationStatus", { length: 50 }).default(
      "pending"
    ), // pending, verified, failed, invalid_email
    createdAt: timestamp("createdAt").defaultNow(),
    updatedAt: timestamp("updatedAt").defaultNow(),
  },
  (table) => ({
    // Constraint: either competitionId OR challengeId must be set, but not both
    compOrChallengeCheck: check(
      "betfred_signups_comp_or_challenge_check",
      sql`(
    (${table.competitionId} IS NOT NULL AND ${table.challengeId} IS NULL) OR
    (${table.competitionId} IS NULL AND ${table.challengeId} IS NOT NULL)
  )`
    ),
    // Indexes for frequently queried columns
    ...{
      idxBetfredSignupsUserId: index("idx_betfred_signups_user_id").on(
        table.userId
      ),
      idxBetfredSignupsCompetitionId: index(
        "idx_betfred_signups_competition_id"
      ).on(table.competitionId),
      idxBetfredSignupsChallengeId: index(
        "idx_betfred_signups_challenge_id"
      ).on(table.challengeId),
      idxBetfredSignupsVerified: index("idx_betfred_signups_verified").on(
        table.verified
      ),
      idxBetfredSignupsVerificationStatus: index(
        "idx_betfred_signups_verification_status"
      ).on(table.verificationStatus),
      idxBetfredSignupsEmail: index("idx_betfred_signups_email").on(
        table.betfredEmail
      ),
    },
  })
);

// Winners tracking table
export const winners = pgTable("winners", {
  id: serial("id").primaryKey(),
  competitionId: integer("competition_id")
    .references(() => competitions.id, { onDelete: "cascade" })
    .notNull(),
  userId: integer("user_id")
    .references(() => users.id, { onDelete: "cascade" })
    .notNull(),
  entryId: integer("entry_id")
    .references(() => entries.id, { onDelete: "cascade" })
    .notNull(),
  selectionMethod: varchar("selection_method")
    .notNull()
    .default("crypto.randomBytes"),
  randomBytes: varchar("random_bytes").notNull(), // hex string of random bytes used
  randomNumber: varchar("random_number").notNull(), // the actual random number generated
  selectedIndex: integer("selected_index").notNull(), // index of winner in entries array
  totalEntries: integer("total_entries").notNull(), // total entries at time of selection
  auditData: jsonb("audit_data"), // additional audit information
  selectedBy: integer("selected_by").references(() => users.id, {
    onDelete: "set null",
  }), // admin who selected
  prizeClaimed: boolean("prize_claimed").default(false).notNull(), // Track if user claimed prize
  claimedDate: timestamp("claimed_date"), // When prize was claimed
  adminNotes: text("admin_notes"), // Quick notes about the winner
  isDeleted: boolean("is_deleted").default(false).notNull(), // soft delete flag
  createdAt: timestamp("created_at").defaultNow(),
});

export const vendors = pgTable("vendors", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  logoUrl: varchar("logo_url", { length: 500 }),
  website: varchar("website", { length: 500 }),
  industry: varchar("industry", { length: 100 }),
  contactEmail: varchar("contact_email", { length: 255 }),
  isActive: boolean("is_active").default(true),
  createdBy: integer("created_by").references(() => users.id, {
    onDelete: "set null",
  }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Terms and Conditions table with versioning support
export const termsAndConditions = pgTable(
  "terms_and_conditions",
  {
    id: serial("id").primaryKey(),
    title: varchar("title", { length: 255 }).notNull(),
    content: text("content").notNull(),
    version: varchar("version", { length: 50 }).notNull().unique(), // Unique constraint to prevent duplicate versions
    isActive: boolean("is_active").default(false).notNull(),
    createdBy: integer("created_by").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_terms_and_conditions_is_active").on(table.isActive),
    index("idx_terms_and_conditions_version").on(table.version),
    index("idx_terms_and_conditions_created_by").on(table.createdBy),
    index("idx_terms_and_conditions_created_at").on(table.createdAt),
    // Partial unique index to ensure only one active version at a time
    index("idx_terms_and_conditions_unique_active")
      .on(table.isActive)
      .where(sql`is_active = true`),
  ]
);

// ============================================================================
// QUESTION ANALYSIS AND USER SEGMENTATION TABLES
// ============================================================================
// AI-powered analysis tables for understanding questions and user segments

// Question Analysis table - stores AI analysis of what each question measures
export const questionAnalysis = pgTable(
  "question_analysis",
  {
    id: serial("id").primaryKey(),
    questionId: integer("question_id").references(() => questions.id, {
      onDelete: "cascade",
    }),
    globalQuestionId: integer("global_question_id").references(
      () => globalQuestions.id,
      { onDelete: "cascade" }
    ),
    brandQuestionId: integer("brand_question_id").references(
      () => brandQuestions.id,
      { onDelete: "cascade" }
    ),
    behavioralQuestionId: integer("behavioral_question_id").references(
      () => behavioralQuestions.id,
      { onDelete: "cascade" }
    ),
    questionText: text("question_text").notNull(), // Cached for performance
    questionType: varchar("question_type").notNull(), // mcq, open, true_false
    analysisType: varchar("analysis_type").notNull(), // measures, insights, patterns
    aiModel: varchar("ai_model").notNull().default("claude-sonnet-4-20250514"),
    analysisResult: jsonb("analysis_result").notNull(), // AI analysis output
    confidence: numeric("confidence", { precision: 3, scale: 2 }), // 0.00-1.00
    tags: text("tags").array(), // identified themes/categories
    createdBy: integer("created_by").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_question_analysis_question_id").on(table.questionId),
    index("idx_question_analysis_global_question_id").on(
      table.globalQuestionId
    ),
    index("idx_question_analysis_brand_question_id").on(table.brandQuestionId),
    index("idx_question_analysis_behavioral_question_id").on(
      table.behavioralQuestionId
    ),
    index("idx_question_analysis_analysis_type").on(table.analysisType),
    index("idx_question_analysis_created_at").on(table.createdAt),
  ]
);

// User Segments table - defines different user segments identified by AI
export const userSegments = pgTable(
  "user_segments",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 255 }).notNull().unique(),
    description: text("description"),
    segmentCriteria: jsonb("segment_criteria").notNull(), // AI-defined criteria
    demographicProfile: jsonb("demographic_profile"), // age ranges, gender distribution, etc.
    behaviorProfile: jsonb("behavior_profile"), // engagement patterns, preferences
    aiModel: varchar("ai_model").notNull().default("claude-sonnet-4-20250514"),
    segmentationDate: timestamp("segmentation_date").defaultNow(),
    isActive: boolean("is_active").default(true),
    userCount: integer("user_count").default(0), // cached count for performance
    createdBy: integer("created_by").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_user_segments_name").on(table.name),
    index("idx_user_segments_is_active").on(table.isActive),
    index("idx_user_segments_segmentation_date").on(table.segmentationDate),
    index("idx_user_segments_created_at").on(table.createdAt),
  ]
);

// User Segment Assignments table - maps users to segments
export const userSegmentAssignments = pgTable(
  "user_segment_assignments",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    segmentId: integer("segment_id")
      .references(() => userSegments.id, { onDelete: "cascade" })
      .notNull(),
    confidence: numeric("confidence", { precision: 3, scale: 2 }), // 0.00-1.00
    assignmentReason: jsonb("assignment_reason"), // AI explanation of why user fits this segment
    aiModel: varchar("ai_model").notNull().default("claude-sonnet-4-20250514"),
    assignedAt: timestamp("assigned_at").defaultNow(),
    isActive: boolean("is_active").default(true), // allow for segment reassignment
    createdBy: integer("created_by").references(() => users.id, {
      onDelete: "set null",
    }),
  },
  (table) => [
    index("idx_user_segment_assignments_user_id").on(table.userId),
    index("idx_user_segment_assignments_segment_id").on(table.segmentId),
    index("idx_user_segment_assignments_is_active").on(table.isActive),
    index("idx_user_segment_assignments_assigned_at").on(table.assignedAt),
    // Unique constraint to prevent duplicate active assignments
    index("idx_user_segment_assignments_unique")
      .on(table.userId, table.segmentId, table.isActive)
      .where(sql`is_active = true`),
  ]
);

// Question Segment Mapping table - maps competition questions to user segments
export const questionSegmentMapping = pgTable(
  "question_segment_mapping",
  {
    id: serial("id").primaryKey(),
    questionId: integer("question_id")
      .references(() => questions.id, { onDelete: "cascade" })
      .notNull(),
    segmentId: integer("segment_id")
      .references(() => userSegments.id, { onDelete: "cascade" })
      .notNull(),
    weight: numeric("weight", { precision: 3, scale: 2 }).default("1.0"),
    positiveAnswers: jsonb("positive_answers").notNull().default("[]"),
    negativeAnswers: jsonb("negative_answers").default("[]"),
    answerSignals: jsonb("answer_signals").notNull().default("[]"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_question_segment_mapping_question").on(table.questionId),
    index("idx_question_segment_mapping_segment").on(table.segmentId),
    index("idx_question_segment_mapping_created_at").on(table.createdAt),
    // Unique constraint to prevent duplicate mappings
    index("idx_question_segment_mapping_unique").on(
      table.questionId,
      table.segmentId
    ),
  ]
);

// Question Insights table - stores AI insights about question-answer combinations
export const questionInsights = pgTable(
  "question_insights",
  {
    id: serial("id").primaryKey(),
    questionId: integer("question_id").references(() => questions.id, {
      onDelete: "cascade",
    }),
    globalQuestionId: integer("global_question_id").references(
      () => globalQuestions.id,
      { onDelete: "cascade" }
    ),
    brandQuestionId: integer("brand_question_id").references(
      () => brandQuestions.id,
      { onDelete: "cascade" }
    ),
    behavioralQuestionId: integer("behavioral_question_id").references(
      () => behavioralQuestions.id,
      { onDelete: "cascade" }
    ),
    answerPattern: varchar("answer_pattern").notNull(), // e.g., "option_a", "contains_keyword_x", "numeric_range"
    segmentId: integer("segment_id").references(() => userSegments.id, {
      onDelete: "cascade",
    }),
    insightType: varchar("insight_type").notNull(), // preference, behavior, demographic_indicator
    insightText: text("insight_text").notNull(), // human-readable insight
    statisticalData: jsonb("statistical_data"), // response rates, correlations, etc.
    confidence: numeric("confidence", { precision: 3, scale: 2 }), // 0.00-1.00
    sampleSize: integer("sample_size"), // number of responses analyzed
    aiModel: varchar("ai_model").notNull().default("claude-sonnet-4-20250514"),
    analysisDate: timestamp("analysis_date").defaultNow(),
    createdBy: integer("created_by").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_question_insights_question_id").on(table.questionId),
    index("idx_question_insights_global_question_id").on(
      table.globalQuestionId
    ),
    index("idx_question_insights_brand_question_id").on(table.brandQuestionId),
    index("idx_question_insights_behavioral_question_id").on(
      table.behavioralQuestionId
    ),
    index("idx_question_insights_segment_id").on(table.segmentId),
    index("idx_question_insights_insight_type").on(table.insightType),
    index("idx_question_insights_analysis_date").on(table.analysisDate),
  ]
);

// User Analysis Cache table - stores cached AI analysis of individual users
export const userAnalysisCache = pgTable(
  "user_analysis_cache",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    analysisType: varchar("analysis_type").notNull(), // personality_profile, preferences, behavior_patterns
    analysisResult: jsonb("analysis_result").notNull(), // AI analysis output
    confidence: numeric("confidence", { precision: 3, scale: 2 }), // 0.00-1.00
    aiModel: varchar("ai_model").notNull().default("claude-sonnet-4-20250514"),
    dataHash: varchar("data_hash"), // hash of input data to detect when re-analysis is needed
    lastUpdated: timestamp("last_updated").defaultNow(),
    validUntil: timestamp("valid_until"), // when this analysis expires
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_user_analysis_cache_user_id").on(table.userId),
    index("idx_user_analysis_cache_analysis_type").on(table.analysisType),
    index("idx_user_analysis_cache_last_updated").on(table.lastUpdated),
    index("idx_user_analysis_cache_valid_until").on(table.validUntil),
    // Unique constraint for user + analysis type
    index("idx_user_analysis_cache_unique").on(
      table.userId,
      table.analysisType
    ),
  ]
);

// ============================================================================
// ANONYMIZED ANALYTICS VIEWS - FOR WAIN READ-ONLY ACCESS
// ============================================================================
// These views provide anonymized analytics data without exposing PII
// All views implement k-anonymity (k>=10) and mask sensitive user data

// v_users_public: Anonymized user metrics aggregated by registration week
export const vUsersPublic = pgView("v_users_public").as((qb) => {
  return qb
    .select({
      registrationWeek: sql<string>`DATE_TRUNC('week', "createdAt")`.as(
        "registration_week"
      ),
      userCount: sql<number>`COUNT(*)`.as("user_count"),
      avgAge:
        sql<number>`ROUND(AVG(CASE WHEN "dateOfBirth" IS NOT NULL THEN EXTRACT(YEAR FROM AGE(CURRENT_DATE, "dateOfBirth")) ELSE NULL END))`.as(
          "avg_age"
        ),
      maleCount: sql<number>`COUNT(CASE WHEN "gender" = 'male' THEN 1 END)`.as(
        "male_count"
      ),
      femaleCount:
        sql<number>`COUNT(CASE WHEN "gender" = 'female' THEN 1 END)`.as(
          "female_count"
        ),
      otherGenderCount:
        sql<number>`COUNT(CASE WHEN "gender" NOT IN ('male', 'female') OR "gender" IS NULL THEN 1 END)`.as(
          "other_gender_count"
        ),
      marketingConsentCount:
        sql<number>`COUNT(CASE WHEN "marketingConsent" = true THEN 1 END)`.as(
          "marketing_consent_count"
        ),
      activeUsersCount:
        sql<number>`COUNT(CASE WHEN "isActive" = true THEN 1 END)`.as(
          "active_users_count"
        ),
      referredUsersCount:
        sql<number>`COUNT(CASE WHEN "referredBy" IS NOT NULL THEN 1 END)`.as(
          "referred_users_count"
        ),
      regions:
        sql<string>`JSON_AGG(DISTINCT CASE WHEN "location" IS NOT NULL THEN SPLIT_PART("location", ',', -1) ELSE NULL END) FILTER (WHERE "location" IS NOT NULL)`.as(
          "regions"
        ),
    })
    .from(users)
    .where(sql`"createdAt" > CURRENT_DATE - INTERVAL '2 years'`)
    .groupBy(sql`DATE_TRUNC('week', "createdAt")`)
    .having(sql`COUNT(*) >= 10`)
    .orderBy(sql`registration_week DESC`);
});

// v_competitions_core: Competition data and performance stats
export const vCompetitionsCore = pgView("v_competitions_core").as((qb) => {
  return qb
    .select({
      id: competitions.id,
      title: competitions.title,
      description: competitions.description,
      category: competitions.category,
      type: competitions.type,
      prizeValue: competitions.prizeValue,
      startDate: competitions.startDate,
      endDate: competitions.endDate,
      isActive: competitions.isActive,
      betfredEnabled: competitions.betfredEnabled,
      dailyStartHour: competitions.dailyStartHour,
      isRecurring: competitions.isRecurring,
      createdAt: competitions.createdAt,
      uniqueParticipants: sql<number>`COUNT(DISTINCT e."userId")`.as(
        "unique_participants"
      ),
      totalEntries: sql<number>`COUNT(e.id)`.as("total_entries"),
      answerEntries:
        sql<number>`COUNT(CASE WHEN e."entryType" = 'answer' THEN 1 END)`.as(
          "answer_entries"
        ),
      shareEntries:
        sql<number>`COUNT(CASE WHEN e."entryType" = 'share' THEN 1 END)`.as(
          "share_entries"
        ),
      referralEntries:
        sql<number>`COUNT(CASE WHEN e."entryType" = 'referral' THEN 1 END)`.as(
          "referral_entries"
        ),
      platformsSharedTo: sql<number>`COUNT(DISTINCT ss.platform)`.as(
        "platforms_shared_to"
      ),
      totalShares: sql<number>`COUNT(ss.id)`.as("total_shares"),
      winnersCount: sql<number>`COUNT(w.id)`.as("winners_count"),
      avgParticipationDelayHours:
        sql<number>`EXTRACT(EPOCH FROM AVG(e."createdAt" - c."startDate"))/3600`.as(
          "avg_participation_delay_hours"
        ),
    })
    .from(competitions)
    .leftJoin(entries, sql`competitions.id = entries."competitionId"`)
    .leftJoin(
      socialShares,
      sql`competitions.id = social_shares."competitionId"`
    )
    .leftJoin(
      winners,
      sql`competitions.id = winners.competition_id AND winners.is_deleted = false`
    )
    .groupBy(
      competitions.id,
      competitions.title,
      competitions.description,
      competitions.category,
      competitions.type,
      competitions.prizeValue,
      competitions.startDate,
      competitions.endDate,
      competitions.isActive,
      competitions.betfredEnabled,
      competitions.dailyStartHour,
      competitions.isRecurring,
      competitions.createdAt
    )
    .orderBy(sql`competitions."createdAt" DESC`);
});

// v_entries_anonymized: Entry patterns and trends without user PII
export const vEntriesAnonymized = pgView("v_entries_anonymized").as((qb) => {
  return qb
    .select({
      entryDate: sql<string>`DATE_TRUNC('day', entries."createdAt")`.as(
        "entry_date"
      ),
      entryType: sql<string>`entries."entryType"`.as("entry_type"),
      platform: sql<string>`entries.platform`.as("platform"),
      competitionCategory: sql<string>`competitions.category`.as(
        "competition_category"
      ),
      competitionType: sql<string>`competitions.type`.as("competition_type"),
      entryCount: sql<number>`COUNT(*)`.as("entry_count"),
      uniqueUsers: sql<number>`COUNT(DISTINCT entries."userId")`.as(
        "unique_users"
      ),
      uniqueCompetitions:
        sql<number>`COUNT(DISTINCT entries."competitionId")`.as(
          "unique_competitions"
        ),
      entryHour: sql<number>`EXTRACT(HOUR FROM entries."createdAt")`.as(
        "entry_hour"
      ),
      dayOfWeek: sql<number>`EXTRACT(DOW FROM entries."createdAt")`.as(
        "day_of_week"
      ),
    })
    .from(entries)
    .leftJoin(competitions, sql`entries."competitionId" = competitions.id`)
    .where(sql`entries."createdAt" > CURRENT_DATE - INTERVAL '90 days'`)
    .groupBy(
      sql`DATE_TRUNC('day', entries."createdAt")`,
      sql`entries."entryType"`,
      sql`entries.platform`,
      sql`competitions.category`,
      sql`competitions.type`,
      sql`EXTRACT(HOUR FROM entries."createdAt")`,
      sql`EXTRACT(DOW FROM entries."createdAt")`
    )
    .having(sql`COUNT(*) >= 5`)
    .orderBy(sql`entry_date DESC`, sql`entry_count DESC`);
});

// v_referrals_summary: Referral analytics with anonymized user data
export const vReferralsSummary = pgView("v_referrals_summary").as((qb) => {
  return qb
    .select({
      referralWeek: sql<string>`DATE_TRUNC('week', referrals."createdAt")`.as(
        "referral_week"
      ),
      competitionCategory: sql<string>`competitions.category`.as(
        "competition_category"
      ),
      competitionType: sql<string>`competitions.type`.as("competition_type"),
      referralStatus: sql<string>`referrals.status`.as("referral_status"),
      referralCount: sql<number>`COUNT(*)`.as("referral_count"),
      uniqueReferrers: sql<number>`COUNT(DISTINCT referrals."referrerId")`.as(
        "unique_referrers"
      ),
      uniqueReferredUsers:
        sql<number>`COUNT(DISTINCT referrals."referredUserId")`.as(
          "unique_referred_users"
        ),
      completedReferrals:
        sql<number>`COUNT(CASE WHEN referrals.status = 'completed' THEN 1 END)`.as(
          "completed_referrals"
        ),
      avgCompletionTimeHours:
        sql<number>`AVG(EXTRACT(EPOCH FROM (referrals."completedAt" - referrals."createdAt"))/3600)`.as(
          "avg_completion_time_hours"
        ),
      completionRatePercent:
        sql<number>`ROUND((COUNT(CASE WHEN referrals.status = 'completed' THEN 1 END)::DECIMAL / NULLIF(COUNT(*), 0)) * 100, 2)`.as(
          "completion_rate_percent"
        ),
    })
    .from(referrals)
    .leftJoin(competitions, sql`referrals."competitionId" = competitions.id`)
    .where(sql`referrals."createdAt" > CURRENT_DATE - INTERVAL '6 months'`)
    .groupBy(
      sql`DATE_TRUNC('week', referrals."createdAt")`,
      sql`competitions.category`,
      sql`competitions.type`,
      sql`referrals.status`
    )
    .having(sql`COUNT(*) >= 10`)
    .orderBy(sql`referral_week DESC`);
});

// v_social_shares_by_platform: Social sharing analytics without user PII
export const vSocialSharesByPlatform = pgView("v_social_shares_by_platform").as(
  (qb) => {
    return qb
      .select({
        shareDate: sql<string>`DATE_TRUNC('day', social_shares."createdAt")`.as(
          "share_date"
        ),
        platform: sql<string>`social_shares.platform`.as("platform"),
        competitionCategory: sql<string>`competitions.category`.as(
          "competition_category"
        ),
        competitionType: sql<string>`competitions.type`.as("competition_type"),
        shareCount: sql<number>`COUNT(*)`.as("share_count"),
        uniqueSharers: sql<number>`COUNT(DISTINCT social_shares."userId")`.as(
          "unique_sharers"
        ),
        uniqueCompetitions:
          sql<number>`COUNT(DISTINCT social_shares."competitionId")`.as(
            "unique_competitions"
          ),
        shareHour: sql<number>`EXTRACT(HOUR FROM social_shares."createdAt")`.as(
          "share_hour"
        ),
        dayOfWeek: sql<number>`EXTRACT(DOW FROM social_shares."createdAt")`.as(
          "day_of_week"
        ),
      })
      .from(socialShares)
      .leftJoin(
        competitions,
        sql`social_shares."competitionId" = competitions.id`
      )
      .where(sql`social_shares."createdAt" > CURRENT_DATE - INTERVAL '60 days'`)
      .groupBy(
        sql`DATE_TRUNC('day', social_shares."createdAt")`,
        sql`social_shares.platform`,
        sql`competitions.category`,
        sql`competitions.type`,
        sql`EXTRACT(HOUR FROM social_shares."createdAt")`,
        sql`EXTRACT(DOW FROM social_shares."createdAt")`
      )
      .having(sql`COUNT(*) >= 3`)
      .orderBy(sql`share_date DESC`, sql`share_count DESC`);
  }
);

// v_winners_summary: Winner statistics without exposing individual winner PII
export const vWinnersSummary = pgView("v_winners_summary").as((qb) => {
  return qb
    .select({
      winMonth: sql<string>`DATE_TRUNC('month', winners.created_at)`.as(
        "win_month"
      ),
      competitionCategory: sql<string>`competitions.category`.as(
        "competition_category"
      ),
      competitionType: sql<string>`competitions.type`.as("competition_type"),
      prizeValue: sql<number>`competitions."prizeValue"`.as("prize_value"),
      winnersCount: sql<number>`COUNT(*)`.as("winners_count"),
      uniqueWinners: sql<number>`COUNT(DISTINCT winners.user_id)`.as(
        "unique_winners"
      ),
      competitionsWithWinners:
        sql<number>`COUNT(DISTINCT winners.competition_id)`.as(
          "competitions_with_winners"
        ),
      prizesClaimed:
        sql<number>`COUNT(CASE WHEN winners.prize_claimed = true THEN 1 END)`.as(
          "prizes_claimed"
        ),
      claimRatePercent:
        sql<number>`ROUND((COUNT(CASE WHEN winners.prize_claimed = true THEN 1 END)::DECIMAL / NULLIF(COUNT(*), 0)) * 100, 2)`.as(
          "claim_rate_percent"
        ),
      avgTotalEntries: sql<number>`AVG(winners.total_entries)`.as(
        "avg_total_entries"
      ),
      avgDaysToClaim:
        sql<number>`AVG(EXTRACT(EPOCH FROM (winners.claimed_date - winners.created_at))/86400)`.as(
          "avg_days_to_claim"
        ),
      selectionMethod: sql<string>`winners.selection_method`.as(
        "selection_method"
      ),
      activeWinners:
        sql<number>`COUNT(CASE WHEN winners.is_deleted = false THEN 1 END)`.as(
          "active_winners"
        ),
    })
    .from(winners)
    .leftJoin(competitions, sql`winners.competition_id = competitions.id`)
    .where(sql`winners.created_at > CURRENT_DATE - INTERVAL '1 year'`)
    .groupBy(
      sql`DATE_TRUNC('month', winners.created_at)`,
      sql`competitions.category`,
      sql`competitions.type`,
      sql`competitions."prizeValue"`,
      sql`winners.selection_method`
    )
    .having(sql`COUNT(*) >= 5`)
    .orderBy(sql`win_month DESC`);
});

// v_daily_metrics: Daily platform KPIs and engagement metrics (simplified for compatibility)
export const vDailyMetrics = pgView("v_daily_metrics").as((qb) => {
  return qb
    .select({
      metricDate: sql<string>`DATE_TRUNC('day', entries."createdAt")`.as(
        "metric_date"
      ),
      totalEntries: sql<number>`COUNT(entries.id)`.as("total_entries"),
      activeUsers: sql<number>`COUNT(DISTINCT entries."userId")`.as(
        "active_users"
      ),
      uniqueCompetitions:
        sql<number>`COUNT(DISTINCT entries."competitionId")`.as(
          "unique_competitions"
        ),
      entryHour: sql<number>`EXTRACT(HOUR FROM entries."createdAt")`.as(
        "entry_hour"
      ),
      dayOfWeek: sql<number>`EXTRACT(DOW FROM entries."createdAt")`.as(
        "day_of_week"
      ),
    })
    .from(entries)
    .where(sql`entries."createdAt" > CURRENT_DATE - INTERVAL '90 days'`)
    .groupBy(
      sql`DATE_TRUNC('day', entries."createdAt")`,
      sql`EXTRACT(HOUR FROM entries."createdAt")`,
      sql`EXTRACT(DOW FROM entries."createdAt")`
    )
    .having(sql`COUNT(*) >= 10`)
    .orderBy(sql`metric_date DESC`);
});

// Relations
export const usersRelations = relations(users, ({ many, one }) => ({
  answers: many(answers),
  entries: many(entries),
  competitions: many(competitions),
  socialShares: many(socialShares),
  spinnerEntries: many(spinnerEntries),
  betfredSignups: many(betfredSignups),
  referralsSent: many(referrals, {
    relationName: "referrer",
  }),
  referralsReceived: many(referrals, {
    relationName: "referred",
  }),
  winnersAsUser: many(winners, {
    relationName: "winner_user",
  }),
  winnersAsSelector: many(winners, {
    relationName: "winner_selector",
  }),
  referrer: one(users, {
    fields: [users.referredBy],
    references: [users.id],
  }),
  // Challenge system relations
  challengeProgress: many(userChallengeProgress),
  challengeActions: many(challengeActions),
  challengeReferralsSent: many(challengeReferrals, {
    relationName: "challenge_referrer",
  }),
  challengeReferralsReceived: many(challengeReferrals, {
    relationName: "challenge_referred",
  }),
  challengesCreated: many(challenges),
}));

export const competitionsRelations = relations(
  competitions,
  ({ one, many }) => ({
    creator: one(users, {
      fields: [competitions.createdBy],
      references: [users.id],
    }),
    questions: many(questions),
    answers: many(answers),
    entries: many(entries),
    socialShares: many(socialShares),
    referrals: many(referrals),
    betfredSignups: many(betfredSignups),
    winners: many(winners),
  })
);

export const globalQuestionsRelations = relations(
  globalQuestions,
  ({ one, many }) => ({
    creator: one(users, {
      fields: [globalQuestions.createdBy],
      references: [users.id],
    }),
    questions: many(questions),
  })
);

export const brandQuestionsRelations = relations(
  brandQuestions,
  ({ one, many }) => ({
    creator: one(users, {
      fields: [brandQuestions.createdBy],
      references: [users.id],
    }),
    vendor: one(vendors, {
      fields: [brandQuestions.vendorId],
      references: [vendors.id],
    }),
    questions: many(questions),
  })
);

export const behavioralQuestionsRelations = relations(
  behavioralQuestions,
  ({ one, many }) => ({
    creator: one(users, {
      fields: [behavioralQuestions.createdBy],
      references: [users.id],
    }),
    questions: many(questions),
  })
);

export const questionsRelations = relations(questions, ({ one, many }) => ({
  competition: one(competitions, {
    fields: [questions.competitionId],
    references: [competitions.id],
  }),
  globalQuestion: one(globalQuestions, {
    fields: [questions.globalQuestionId],
    references: [globalQuestions.id],
  }),
  brandQuestion: one(brandQuestions, {
    fields: [questions.brandQuestionId],
    references: [brandQuestions.id],
  }),
  behavioralQuestion: one(behavioralQuestions, {
    fields: [questions.behavioralQuestionId],
    references: [behavioralQuestions.id],
  }),
  answers: many(answers),
}));

export const answersRelations = relations(answers, ({ one }) => ({
  question: one(questions, {
    fields: [answers.questionId],
    references: [questions.id],
  }),
  user: one(users, {
    fields: [answers.userId],
    references: [users.id],
  }),
  competition: one(competitions, {
    fields: [answers.competitionId],
    references: [competitions.id],
  }),
}));

export const entriesRelations = relations(entries, ({ one }) => ({
  user: one(users, {
    fields: [entries.userId],
    references: [users.id],
  }),
  competition: one(competitions, {
    fields: [entries.competitionId],
    references: [competitions.id],
  }),
}));

export const socialSharesRelations = relations(socialShares, ({ one }) => ({
  user: one(users, {
    fields: [socialShares.userId],
    references: [users.id],
  }),
  competition: one(competitions, {
    fields: [socialShares.competitionId],
    references: [competitions.id],
  }),
  entry: one(entries, {
    fields: [socialShares.entryId],
    references: [entries.id],
  }),
}));

export const referralsRelations = relations(referrals, ({ one }) => ({
  referrer: one(users, {
    fields: [referrals.referrerId],
    references: [users.id],
    relationName: "referrer",
  }),
  referredUser: one(users, {
    fields: [referrals.referredUserId],
    references: [users.id],
    relationName: "referred",
  }),
  competition: one(competitions, {
    fields: [referrals.competitionId],
    references: [competitions.id],
  }),
  referrerEntry: one(entries, {
    fields: [referrals.referrerEntryId],
    references: [entries.id],
  }),
  referredEntry: one(entries, {
    fields: [referrals.referredEntryId],
    references: [entries.id],
  }),
}));

export const winnersRelations = relations(winners, ({ one }) => ({
  competition: one(competitions, {
    fields: [winners.competitionId],
    references: [competitions.id],
  }),
  user: one(users, {
    fields: [winners.userId],
    references: [users.id],
    relationName: "winner_user",
  }),
  entry: one(entries, {
    fields: [winners.entryId],
    references: [entries.id],
  }),
  selector: one(users, {
    fields: [winners.selectedBy],
    references: [users.id],
    relationName: "winner_selector",
  }),
}));

export const betfredSignupsRelations = relations(betfredSignups, ({ one }) => ({
  user: one(users, {
    fields: [betfredSignups.userId],
    references: [users.id],
  }),
  competition: one(competitions, {
    fields: [betfredSignups.competitionId],
    references: [competitions.id],
  }),
  challenge: one(challenges, {
    fields: [betfredSignups.challengeId],
    references: [challenges.id],
  }),
}));

// Roadmap table
export const roadmapItems = pgTable("roadmap_items", {
  id: serial("id").primaryKey(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  status: varchar("status", { length: 50 }).notNull().default("planned"), // planned, in_progress, completed, backlog
  priority: varchar("priority", { length: 20 }).notNull().default("medium"), // low, medium, high, critical
  category: varchar("category", { length: 50 }).notNull().default("feature"), // feature, bug, improvement, task
  targetDate: timestamp("target_date"),
  completedDate: timestamp("completed_date"),
  assignedTo: varchar("assigned_to", { length: 255 }), // Stores user ID as string
  tags: text("tags").array(),
  isGlobal: boolean("is_global").notNull().default(true), // Global roadmap items visible to all admins
  createdBy: integer("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const roadmapItemsRelations = relations(roadmapItems, ({ one }) => ({
  creator: one(users, {
    fields: [roadmapItems.createdBy],
    references: [users.id],
  }),
}));

// Move all schemas to be defined after all table definitions

export const systemSettings = pgTable("system_settings", {
  id: serial("id").primaryKey(),
  key: text("key").notNull().unique(),
  value: text("value").notNull(),
  description: text("description"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedBy: integer("updated_by").references(() => users.id),
});

// Tracking codes table for header and footer management
export const trackingCodes = pgTable("tracking_codes", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 255 }).notNull(), // e.g., "Google Tag Manager", "Facebook Pixel"
  code: text("code").notNull(), // The actual code snippet
  position: varchar("position", { length: 10 }).notNull(), // "head" or "body"
  isActive: boolean("is_active").default(true).notNull(),
  description: text("description"), // Optional description for the admin
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: integer("created_by").references(() => users.id),
});

// Blog posts table
export const blogPosts = pgTable("blog_posts", {
  id: serial("id").primaryKey(),
  title: varchar("title", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 255 }).notNull().unique(),
  excerpt: text("excerpt"), // Short description for listings
  content: text("content").notNull(), // Full blog post content
  metaTitle: varchar("meta_title", { length: 60 }), // SEO title (60 chars max)
  metaDescription: varchar("meta_description", { length: 160 }), // SEO description (160 chars max)
  keywords: text("keywords"), // SEO keywords, comma separated
  featuredImage: varchar("featured_image", { length: 500 }), // URL to featured image
  imageAlt: varchar("image_alt", { length: 255 }), // Alt text for featured image
  status: varchar("status", { length: 20 }).notNull().default("draft"), // draft, published, archived
  category: varchar("category", { length: 100 }), // Blog category
  tags: text("tags").array(), // Array of tags
  readTime: integer("read_time"), // Estimated reading time in minutes
  viewCount: integer("view_count").default(0), // Track page views
  isPublished: boolean("is_published").default(false).notNull(),
  publishedAt: timestamp("published_at"),
  authorId: integer("author_id")
    .references(() => users.id)
    .notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Competition Coupons - Allow granting bonus entries via codes
export const competitionCoupons = pgTable(
  "competition_coupons",
  {
    id: serial("id").primaryKey(),
    competitionId: integer("competition_id")
      .references(() => competitions.id, { onDelete: "cascade" })
      .notNull(),
    code: varchar("code", { length: 50 }).notNull(),
    entriesPerRedemption: integer("entries_per_redemption")
      .notNull()
      .default(1),
    maxRedemptionsTotal: integer("max_redemptions_total"),
    maxRedemptionsPerUser: integer("max_redemptions_per_user").default(1),
    isUniversal: boolean("is_universal").default(false).notNull(),
    validFrom: timestamp("valid_from"),
    validUntil: timestamp("valid_until"),
    status: varchar("status", { length: 20 }).notNull().default("active"), // active, inactive, expired
    metadata: jsonb("metadata"),
    createdBy: integer("created_by").references(() => users.id),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_competition_coupons_competition_id").on(table.competitionId),
    index("idx_competition_coupons_code").on(table.code),
    uniqueIndex("uq_competition_coupons_competition_code").on(
      table.competitionId,
      table.code
    ),
  ]
);

export const competitionCouponRedemptions = pgTable(
  "competition_coupon_redemptions",
  {
    id: serial("id").primaryKey(),
    couponId: integer("coupon_id")
      .references(() => competitionCoupons.id, { onDelete: "cascade" })
      .notNull(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    competitionId: integer("competition_id")
      .references(() => competitions.id, { onDelete: "cascade" })
      .notNull(),
    entriesGranted: integer("entries_granted").notNull(),
    redeemedAt: timestamp("redeemed_at").defaultNow(),
    sourceIp: varchar("source_ip", { length: 45 }),
    userAgent: text("user_agent"),
    metadata: jsonb("metadata"),
  },
  (table) => [
    index("idx_competition_coupon_redemptions_coupon_id").on(table.couponId),
    index("idx_competition_coupon_redemptions_user_id").on(table.userId),
    index("idx_competition_coupon_redemptions_competition_id").on(
      table.competitionId
    ),
  ]
);

export const blogPostsRelations = relations(blogPosts, ({ one }) => ({
  author: one(users, {
    fields: [blogPosts.authorId],
    references: [users.id],
  }),
}));

export const competitionCouponsRelations = relations(
  competitionCoupons,
  ({ one, many }) => ({
    competition: one(competitions, {
      fields: [competitionCoupons.competitionId],
      references: [competitions.id],
    }),
    createdByUser: one(users, {
      fields: [competitionCoupons.createdBy],
      references: [users.id],
    }),
    redemptions: many(competitionCouponRedemptions),
  })
);

export const competitionCouponRedemptionsRelations = relations(
  competitionCouponRedemptions,
  ({ one }) => ({
    coupon: one(competitionCoupons, {
      fields: [competitionCouponRedemptions.couponId],
      references: [competitionCoupons.id],
    }),
    user: one(users, {
      fields: [competitionCouponRedemptions.userId],
      references: [users.id],
    }),
    competition: one(competitions, {
      fields: [competitionCouponRedemptions.competitionId],
      references: [competitions.id],
    }),
  })
);

// Schemas moved to end of file

// Types moved to end of file

// Settings and Configuration
export const settings = pgTable("settings", {
  id: serial("id").primaryKey(),
  key: varchar("key", { length: 255 }).notNull().unique(),
  value: text("value").notNull(),
  category: varchar("category", { length: 100 }).notNull(), // system, security, integration, notification
  description: text("description"),
  dataType: varchar("data_type", { length: 50 }).notNull().default("string"), // string, number, boolean, json
  isPublic: boolean("is_public").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const roles = pgTable("roles", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 100 }).notNull().unique(),
  description: text("description"),
  permissions: text("permissions")
    .array()
    .notNull()
    .default(sql`ARRAY[]::text[]`),
  isSystem: boolean("is_system").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const userRoles = pgTable("user_roles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .references(() => users.id)
    .notNull(),
  roleId: integer("role_id")
    .references(() => roles.id)
    .notNull(),
  assignedBy: integer("assigned_by").references(() => users.id),
  assignedAt: timestamp("assigned_at").defaultNow(),
});

// Spinner Entries table for tracking entry wins
export const spinnerEntries = pgTable("spinner_entries", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .references(() => users.id)
    .notNull(),
  entriesWon: integer("entries_won").notNull(),
  spinType: varchar("spin_type", { length: 50 }).notNull().default("daily"), // daily, bonus, etc
  createdAt: timestamp("created_at").defaultNow(),
});

// Spinner Results table for tracking what users can win
export const spinnerResults = pgTable("spinner_results", {
  id: serial("id").primaryKey(),
  label: varchar("label").notNull(), // Display text like "100 Entries!" or "Better luck next time!"
  entries: integer("entries").notNull().default(0), // Number of entries to award
  probability: integer("probability").notNull(), // Probability out of 10000 (for 0.01% precision)
  color: varchar("color").notNull().default("#3b82f6"), // Color for the spinner segment
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const auditLogs = pgTable("audit_logs", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id),
  action: varchar("action", { length: 100 }).notNull(),
  resource: varchar("resource", { length: 100 }).notNull(),
  resourceId: integer("resource_id"),
  changes: jsonb("changes"),
  ipAddress: varchar("ip_address", { length: 45 }),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Bug Tracking
export const bugs = pgTable("bugs", {
  id: serial("id").primaryKey(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description").notNull(),
  reproductionSteps: text("reproduction_steps"),
  expectedBehavior: text("expected_behavior"),
  actualBehavior: text("actual_behavior"),
  environment: jsonb("environment"), // { os, browser, version, etc }
  priority: varchar("priority", { length: 20 }).notNull(), // critical, high, medium, low
  status: varchar("status", { length: 50 }).notNull().default("open"), // open, in_progress, testing, resolved, closed, reopened
  severity: varchar("severity", { length: 20 }), // blocker, major, minor, trivial
  category: varchar("category", { length: 100 }), // UI, backend, database, performance, security
  assignedTo: integer("assigned_to").references(() => users.id),
  reportedBy: integer("reported_by")
    .references(() => users.id)
    .notNull(),
  resolvedBy: integer("resolved_by").references(() => users.id),
  resolvedAt: timestamp("resolved_at"),
  resolution: text("resolution"),
  affectedVersion: varchar("affected_version", { length: 50 }),
  fixVersion: varchar("fix_version", { length: 50 }),
  tags: text("tags")
    .array()
    .default(sql`ARRAY[]::text[]`),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const bugAttachments = pgTable("bug_attachments", {
  id: serial("id").primaryKey(),
  bugId: integer("bug_id")
    .references(() => bugs.id, { onDelete: "cascade" })
    .notNull(),
  fileName: varchar("file_name", { length: 255 }).notNull(),
  fileUrl: text("file_url").notNull(),
  fileSize: integer("file_size"),
  mimeType: varchar("mime_type", { length: 100 }),
  uploadedBy: integer("uploaded_by")
    .references(() => users.id)
    .notNull(),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
});

export const bugComments = pgTable("bug_comments", {
  id: serial("id").primaryKey(),
  bugId: integer("bug_id")
    .references(() => bugs.id, { onDelete: "cascade" })
    .notNull(),
  userId: integer("user_id")
    .references(() => users.id)
    .notNull(),
  comment: text("comment").notNull(),
  isInternal: boolean("is_internal").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Test Case Management
export const testCases = pgTable("test_cases", {
  id: serial("id").primaryKey(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  feature: varchar("feature", { length: 100 }).notNull(),
  preconditions: text("preconditions"),
  testSteps: text("test_steps"), // Simple text field for test steps
  expectedResults: text("expected_results"),
  priority: varchar("priority", { length: 20 }).notNull(), // critical, high, medium, low
  status: varchar("status", { length: 50 }).notNull().default("draft"), // draft, active, deprecated
  type: varchar("type", { length: 50 }), // functional, regression, smoke, acceptance
  estimatedTime: integer("estimated_time"), // in minutes
  automationStatus: varchar("automation_status", { length: 50 }), // manual, automated, partial
  tags: text("tags")
    .array()
    .default(sql`ARRAY[]::text[]`),
  createdBy: integer("created_by")
    .references(() => users.id)
    .notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const testRuns = pgTable("test_runs", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  status: varchar("status", { length: 50 }).notNull().default("in_progress"), // planned, in_progress, completed, cancelled
  environment: varchar("environment", { length: 100 }),
  version: varchar("version", { length: 50 }),
  executedBy: integer("executed_by")
    .references(() => users.id)
    .notNull(),
});

export const testExecutions = pgTable("test_executions", {
  id: serial("id").primaryKey(),
  testRunId: integer("test_run_id")
    .references(() => testRuns.id, { onDelete: "cascade" })
    .notNull(),
  testCaseId: integer("test_case_id")
    .references(() => testCases.id)
    .notNull(),
  status: varchar("status", { length: 50 }).notNull(), // passed, failed, blocked, skipped
  actualResults: text("actual_results"),
  notes: text("notes"),
  executionTime: integer("execution_time"), // in seconds
  executedBy: integer("executed_by")
    .references(() => users.id)
    .notNull(),
  executedAt: timestamp("executed_at").defaultNow(),
});

// Advanced Roadmap Features
export const roadmapDependencies = pgTable("roadmap_dependencies", {
  id: serial("id").primaryKey(),
  dependentId: integer("dependent_id")
    .references(() => roadmapItems.id, { onDelete: "cascade" })
    .notNull(),
  dependsOnId: integer("depends_on_id")
    .references(() => roadmapItems.id, { onDelete: "cascade" })
    .notNull(),
  type: varchar("type", { length: 50 }).notNull().default("blocks"), // blocks, related
  createdAt: timestamp("created_at").defaultNow(),
});

export const roadmapComments = pgTable("roadmap_comments", {
  id: serial("id").primaryKey(),
  roadmapItemId: integer("roadmap_item_id")
    .references(() => roadmapItems.id, { onDelete: "cascade" })
    .notNull(),
  userId: integer("user_id")
    .references(() => users.id)
    .notNull(),
  comment: text("comment").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const roadmapAttachments = pgTable("roadmap_attachments", {
  id: serial("id").primaryKey(),
  roadmapItemId: integer("roadmap_item_id")
    .references(() => roadmapItems.id, { onDelete: "cascade" })
    .notNull(),
  fileName: varchar("file_name", { length: 255 }).notNull(),
  fileUrl: text("file_url").notNull(),
  fileSize: integer("file_size"),
  mimeType: varchar("mime_type", { length: 100 }),
  uploadedBy: integer("uploaded_by")
    .references(() => users.id)
    .notNull(),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
});

// Social Media Management
export const socialChannels = pgTable("social_channels", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 100 }).notNull(),
  platform: varchar("platform", { length: 50 }).notNull(), // facebook, x, instagram, linkedin, youtube, tiktok
  accountHandle: varchar("account_handle", { length: 100 }).notNull(),
  accountId: varchar("account_id", { length: 255 }),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  tokenExpiresAt: timestamp("token_expires_at"),
  isActive: boolean("is_active").default(true),
  profileImage: varchar("profile_image"),
  followerCount: integer("follower_count").default(0),
  lastSyncedAt: timestamp("last_synced_at"),
  settings: jsonb("settings"), // platform-specific settings
  timezone: varchar("timezone", { length: 50 }).default("UTC"),
  optimalPostTimes: jsonb("optimal_post_times"), // store best posting times analysis
  createdBy: integer("created_by")
    .references(() => users.id)
    .notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const socialPosts = pgTable("social_posts", {
  id: serial("id").primaryKey(),
  title: varchar("title", { length: 255 }),
  content: text("content").notNull(),
  mediaUrls: text("media_urls")
    .array()
    .default(sql`ARRAY[]::text[]`),
  platforms: text("platforms").array().notNull(), // which platforms to post to
  status: varchar("status", { length: 50 }).notNull().default("draft"), // draft, scheduled, posted, failed
  scheduledAt: timestamp("scheduled_at"),
  postedAt: timestamp("posted_at"),
  generatedByAi: boolean("generated_by_ai").default(false),
  aiPrompt: text("ai_prompt"),
  hashtags: text("hashtags")
    .array()
    .default(sql`ARRAY[]::text[]`),
  metadata: jsonb("metadata"), // engagement data, post IDs per platform
  autoScheduled: boolean("auto_scheduled").default(false),
  optimalTiming: boolean("optimal_timing").default(false),
  platformAdapted: boolean("platform_adapted").default(false),
  adaptedContent: jsonb("adapted_content"), // platform-specific content variations
  createdBy: integer("created_by")
    .references(() => users.id)
    .notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const socialAnalytics = pgTable("social_analytics", {
  id: serial("id").primaryKey(),
  channelId: integer("channel_id")
    .references(() => socialChannels.id)
    .notNull(),
  postId: integer("post_id").references(() => socialPosts.id),
  platform: varchar("platform", { length: 50 }).notNull(),
  platformPostId: varchar("platform_post_id", { length: 255 }),
  metrics: jsonb("metrics").notNull(), // likes, shares, comments, views, etc.
  date: date("date").notNull(),
  impressions: integer("impressions").default(0),
  engagements: integer("engagements").default(0),
  reach: integer("reach").default(0),
  clicks: integer("clicks").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const socialTemplates = pgTable("social_templates", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  template: text("template").notNull(),
  category: varchar("category", { length: 50 }), // promotion, announcement, engagement, competition
  platforms: text("platforms").array().notNull(),
  variables: jsonb("variables"), // template variables like {competition_name}, {prize}
  isActive: boolean("is_active").default(true),
  createdBy: integer("created_by")
    .references(() => users.id)
    .notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Social Media Scheduling and Optimization Tables
export const socialSchedulingRules = pgTable("social_scheduling_rules", {
  id: serial("id").primaryKey(),
  channelId: integer("channel_id")
    .references(() => socialChannels.id)
    .notNull(),
  platform: varchar("platform", { length: 50 }).notNull(),
  dayOfWeek: integer("day_of_week").notNull(), // 0-6 (Sunday-Saturday)
  hourOfDay: integer("hour_of_day").notNull(), // 0-23
  engagementScore: integer("engagement_score").default(0), // historical performance score
  isOptimal: boolean("is_optimal").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const socialContentCalendar = pgTable("social_content_calendar", {
  id: serial("id").primaryKey(),
  postId: integer("post_id")
    .references(() => socialPosts.id)
    .notNull(),
  channelId: integer("channel_id")
    .references(() => socialChannels.id)
    .notNull(),
  platform: varchar("platform", { length: 50 }).notNull(),
  scheduledDate: date("scheduled_date").notNull(),
  scheduledTime: varchar("scheduled_time", { length: 5 }).notNull(), // HH:MM format
  status: varchar("status", { length: 50 }).notNull().default("scheduled"), // scheduled, posted, failed, cancelled
  priority: integer("priority").default(1), // 1-5 priority level
  campaignId: varchar("campaign_id", { length: 100 }), // for grouping related posts
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const socialAudienceInsights = pgTable("social_audience_insights", {
  id: serial("id").primaryKey(),
  channelId: integer("channel_id")
    .references(() => socialChannels.id)
    .notNull(),
  platform: varchar("platform", { length: 50 }).notNull(),
  ageGroup: varchar("age_group", { length: 50 }),
  gender: varchar("gender", { length: 20 }),
  location: varchar("location", { length: 100 }),
  activeHours: jsonb("active_hours"), // hourly activity data
  peakDays: text("peak_days")
    .array()
    .default(sql`ARRAY[]::text[]`), // most active days
  engagementRate: integer("engagement_rate").default(0),
  dataDate: date("data_date").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Multi-tenant support
export const organizations = pgTable("organizations", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 100 }).notNull().unique(),
  settings: jsonb("settings"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const userOrganizations = pgTable("user_organizations", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .references(() => users.id)
    .notNull(),
  organizationId: integer("organization_id")
    .references(() => organizations.id)
    .notNull(),
  role: varchar("role", { length: 50 }).notNull().default("member"), // owner, admin, member
  joinedAt: timestamp("joined_at").defaultNow(),
});

// Communications table for email management
export const communications = pgTable("communications", {
  id: serial("id").primaryKey(),
  type: varchar("type", { length: 50 })
    .notNull()
    .default("winner_notification"),
  recipientEmail: varchar("recipient_email", { length: 255 }).notNull(),
  recipientName: varchar("recipient_name", { length: 255 }),
  subject: varchar("subject", { length: 500 }).notNull(),
  body: text("body").notNull(),
  bccEmails: text("bcc_emails"), // Comma-separated BCC email addresses
  competitionId: integer("competition_id").references(() => competitions.id),
  winnerId: integer("winner_id").references(() => winners.id),
  status: varchar("status", { length: 50 }).notNull().default("draft"),
  createdBy: integer("created_by").references(() => users.id),
  sentAt: timestamp("sent_at"),
  isDeleted: boolean("is_deleted").default(false).notNull(), // soft delete flag
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ===== CHALLENGES & LEADERBOARD SYSTEM =====
// Separate challenge system that doesn't interfere with existing competitions

// Challenges table - main challenge definitions
export const challenges = pgTable("challenges", {
  id: serial("id").primaryKey(),
  type: varchar("type", { length: 20 }).notNull(), // 'acquisition', 'participation', 'growth', 'marketing'
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  startDatetime: timestamp("start_datetime", { withTimezone: true }).notNull(),
  endDatetime: timestamp("end_datetime", { withTimezone: true }), // Optional - challenges end when target reached
  status: varchar("status", { length: 20 }).notNull().default("draft"), // 'draft', 'scheduled', 'live', 'ended'
  rulesJson: jsonb("rules_json").notNull(),
  prize1: text("prize1"), // First prize
  prize2: text("prize2"), // Second prize
  prize3: text("prize3"), // Third prize
  targetValue: integer("target_value"), // For race challenges (e.g., 1000 referrals)
  isActive: boolean("is_active").default(true),
  isVisible: boolean("is_visible").default(true), // Controls frontend display visibility
  customRules: text("custom_rules"), // Custom rules text for challenge-specific rules
  leftImageUrl: text("left_image_url"), // URL for left hero image
  rightImageUrl: text("right_image_url"), // URL for right hero image
  reviewMilestones: text("review_milestones"), // Comma-separated review milestone numbers (e.g., "50,100,250,500,750")
  createdBy: integer("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// User Challenge Progress - tracks individual user progress in challenges
export const userChallengeProgress = pgTable(
  "user_challenge_progress",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    challengeId: integer("challenge_id")
      .references(() => challenges.id, { onDelete: "cascade" })
      .notNull(),
    progressCount: integer("progress_count").default(0).notNull(),
    completed: boolean("completed").default(false).notNull(),
    joinedAt: timestamp("joined_at").defaultNow(),
    lastUpdated: timestamp("last_updated").defaultNow(),
  },
  (table) => [
    // Ensure one progress record per user per challenge
    index("idx_user_challenge_unique").on(table.userId, table.challengeId),
  ]
);

// Leaderboard Cache - cached rankings for performance
export const leaderboardCache = pgTable("leaderboard_cache", {
  challengeId: integer("challenge_id")
    .primaryKey()
    .references(() => challenges.id, { onDelete: "cascade" }),
  rankingsJson: jsonb("rankings_json").notNull(),
  totalParticipants: integer("total_participants").default(0).notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Challenge Actions Log - tracks all challenge-related actions for audit/fraud detection
export const challengeActions = pgTable(
  "challenge_actions",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id").references(() => users.id),
    challengeId: integer("challenge_id").references(() => challenges.id),
    actionType: varchar("action_type", { length: 50 }).notNull(),
    actionData: jsonb("action_data"),
    pointsEarned: integer("points_earned").default(0),
    ipAddress: varchar("ip_address", { length: 45 }),
    userAgent: text("user_agent"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_challenge_actions_user").on(table.userId),
    index("idx_challenge_actions_challenge").on(table.challengeId),
    index("idx_challenge_actions_type").on(table.actionType),
    // IDEMPOTENCY CONSTRAINT: Prevent duplicate action logging for same referral/action
    unique("uniq_challenge_actions_referral_action").on(
      table.userId,
      table.challengeId,
      table.actionType
    ),
  ]
);

// Challenge Referrals - separate referral tracking for challenges
export const challengeReferrals = pgTable(
  "challenge_referrals",
  {
    id: serial("id").primaryKey(),
    challengeId: integer("challenge_id")
      .references(() => challenges.id, { onDelete: "cascade" })
      .notNull(),
    referrerId: integer("referrer_id")
      .references(() => users.id)
      .notNull(),
    referredEmail: varchar("referred_email", { length: 255 }).notNull(),
    referredUserId: integer("referred_user_id").references(() => users.id),
    referralCode: varchar("referral_code", { length: 20 }).notNull(),
    status: varchar("status", { length: 30 }).notNull().default("pending"), // 'pending', 'pending_verification', 'verified', 'rejected', 'self'
    socialSource: varchar("social_source", { length: 10 }), // 'fb', 'wa', 'x', 'direct'
    verifiedAt: timestamp("verified_at"),
    ipAddress: varchar("ip_address", { length: 45 }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_challenge_referrals_referrer").on(table.referrerId),
    index("idx_challenge_referrals_code").on(table.referralCode),
    index("idx_challenge_referrals_email").on(table.referredEmail),
    // SECURITY CONSTRAINT: Prevent duplicate referrals for the same user in the same challenge
    unique("uniq_challenge_referrals_challenge_user").on(
      table.challengeId,
      table.referredUserId
    ),
  ]
);

// Pending Referrals - temporary storage for referral data (replaces cookies)
export const pendingReferrals = pgTable(
  "pending_referrals",
  {
    id: serial("id").primaryKey(),
    challengeId: integer("challenge_id")
      .references(() => challenges.id, { onDelete: "cascade" })
      .notNull(),
    referralCode: varchar("referral_code", { length: 20 }).notNull(),
    socialSource: varchar("social_source", { length: 10 }), // 'fb', 'wa', 'x', 'direct'
    ipAddress: varchar("ip_address", { length: 45 }),
    userAgent: text("user_agent"),
    sessionId: varchar("session_id", { length: 255 }), // For tracking browser session
    processed: boolean("processed").default(false).notNull(), // Track if referral was processed
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(), // TTL for cleanup - 7 days from creation
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_pending_referrals_challenge").on(table.challengeId),
    index("idx_pending_referrals_code").on(table.referralCode),
    index("idx_pending_referrals_session").on(table.sessionId),
    index("idx_pending_referrals_expires").on(table.expiresAt), // For cleanup queries
    index("idx_pending_referrals_processed").on(table.processed), // For cleanup queries
  ]
);

// Challenge Winners - separate winner tracking for challenges (different from competitions)
export const challengeWinners = pgTable(
  "challenge_winners",
  {
    id: serial("id").primaryKey(),
    challengeId: integer("challenge_id")
      .references(() => challenges.id, { onDelete: "cascade" })
      .notNull(),
    userId: integer("user_id")
      .references(() => users.id)
      .notNull(),
    position: integer("position").notNull(), // 1 = first place, 2 = second place, 3 = third place
    progressCount: integer("progress_count").notNull(), // Progress when they won
    referralCount: integer("referral_count").default(0).notNull(), // Verified referrals at time of winning
    winningType: varchar("winning_type", { length: 20 }).notNull(), // 'target_reached' or 'referral_ranking'
    wonAt: timestamp("won_at").defaultNow(),
    prize: text("prize"), // Copy of prize1/prize2/prize3 from challenge
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_challenge_winners_challenge").on(table.challengeId),
    index("idx_challenge_winners_user").on(table.userId),
    index("idx_challenge_winners_position").on(table.position),
    index("idx_challenge_winners_won_at").on(table.wonAt),
    // Ensure one position per challenge (unique winner per position)
    unique("uniq_challenge_winners_challenge_position").on(
      table.challengeId,
      table.position
    ),
  ]
);

// WAIN AI Chat tables
export const aiConversations = pgTable(
  "ai_conversations",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    title: varchar("title", { length: 255 }).default("New Chat"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_ai_conversations_user_id").on(table.userId),
    index("idx_ai_conversations_created_at").on(table.createdAt),
  ]
);

export const aiMessages = pgTable(
  "ai_messages",
  {
    id: serial("id").primaryKey(),
    conversationId: integer("conversation_id")
      .references(() => aiConversations.id, { onDelete: "cascade" })
      .notNull(),
    role: varchar("role", { length: 20 }).notNull(), // 'user' or 'assistant' - validated in backend
    content: text("content").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_ai_messages_conversation_id").on(table.conversationId),
    index("idx_ai_messages_conversation_created").on(
      table.conversationId,
      table.createdAt
    ),
  ]
);

// AI Artifacts table for storing generated content (code, HTML, documents, etc.)
export const aiArtifacts = pgTable(
  "ai_artifacts",
  {
    id: serial("id").primaryKey(),
    conversationId: integer("conversation_id")
      .references(() => aiConversations.id, { onDelete: "cascade" })
      .notNull(),
    messageId: integer("message_id").references(() => aiMessages.id, {
      onDelete: "cascade",
    }),
    title: varchar("title", { length: 255 }).notNull(),
    description: text("description"),
    type: varchar("type", { length: 50 }).notNull(), // 'code', 'html', 'document', 'report', 'json', 'csv', 'sql'
    language: varchar("language", { length: 50 }), // For code artifacts: 'javascript', 'python', 'sql', 'html', 'css', etc.
    content: text("content").notNull(),
    isPublic: boolean("is_public").default(false),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_ai_artifacts_conversation_id").on(table.conversationId),
    index("idx_ai_artifacts_message_id").on(table.messageId),
    index("idx_ai_artifacts_type").on(table.type),
    index("idx_ai_artifacts_created_at").on(table.createdAt),
  ]
);

// Share Intents table - tracks share verification attempts
export const shareIntents = pgTable(
  "share_intents",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    competitionId: integer("competition_id")
      .references(() => competitions.id, { onDelete: "cascade" })
      .notNull(),
    platform: varchar("platform", { length: 50 }).notNull(),
    intentType: varchar("intent_type", { length: 50 })
      .default("share")
      .notNull(),
    status: varchar("status", { length: 50 }).default("pending").notNull(),
    metadata: jsonb("metadata"),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_share_intents_user_competition").on(
      table.userId,
      table.competitionId
    ),
    index("idx_share_intents_status").on(table.status),
    index("idx_share_intents_expires_at").on(table.expiresAt),
    check(
      "check_share_intents_platform",
      sql`${table.platform} IN ('twitter', 'instagram', 'tiktok', 'facebook', 'linkedin')`
    ),
    check(
      "check_share_intents_status",
      sql`${table.status} IN ('pending', 'expired', 'used', 'cancelled')`
    ),
  ]
);

// Share Verifications table - verified shares with bonus tracking
export const shareVerifications = pgTable(
  "share_verifications",
  {
    id: serial("id").primaryKey(),
    shareIntentId: integer("share_intent_id")
      .references(() => shareIntents.id, { onDelete: "cascade" })
      .notNull(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    competitionId: integer("competition_id")
      .references(() => competitions.id, { onDelete: "cascade" })
      .notNull(),
    platform: varchar("platform", { length: 50 }).notNull(),
    socialUrl: text("social_url").notNull(),
    verificationStatus: varchar("verification_status", { length: 50 })
      .default("pending")
      .notNull(),
    hashtagsFound: text("hashtags_found")
      .array()
      .default(sql`ARRAY[]::text[]`),
    verificationData: jsonb("verification_data"),
    bonusEntriesAwarded: integer("bonus_entries_awarded").default(0),
    platformMultiplier: numeric("platform_multiplier", {
      precision: 3,
      scale: 2,
    }).default("1.00"),
    verifiedAt: timestamp("verified_at"),
    verificationError: text("verification_error"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_share_verifications_user_competition").on(
      table.userId,
      table.competitionId
    ),
    index("idx_share_verifications_intent_id").on(table.shareIntentId),
    index("idx_share_verifications_status").on(table.verificationStatus),
    index("idx_share_verifications_platform").on(table.platform),
    index("idx_share_verifications_verified_at").on(table.verifiedAt),
    check(
      "check_share_verifications_platform",
      sql`${table.platform} IN ('twitter', 'instagram', 'tiktok', 'facebook', 'linkedin')`
    ),
    check(
      "check_share_verifications_status",
      sql`${table.verificationStatus} IN ('pending', 'verified', 'failed', 'invalid_url', 'hashtag_missing', 'expired')`
    ),
  ]
);

// ============================================================================
// CRM EMAIL MARKETING SYSTEM TABLES
// ============================================================================

// Email Templates - Reusable email templates with variable placeholders
export const emailTemplates = pgTable(
  "email_templates",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 255 }).notNull(),
    subject: varchar("subject", { length: 255 }).notNull(),
    bodyHtml: text("body_html").notNull(),
    bodyText: text("body_text"),
    variables: jsonb("variables"), // Array of variable placeholders e.g., [{"name": "firstName", "type": "string", "default": ""}]
    category: varchar("category", { length: 100 }).default("general"), // general, welcome, tier_advancement, share_verification, etc.
    isActive: boolean("is_active").default(true),
    createdBy: integer("created_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_email_templates_category").on(table.category),
    index("idx_email_templates_is_active").on(table.isActive),
    index("idx_email_templates_created_by").on(table.createdBy),
    index("idx_email_templates_created_at").on(table.createdAt),
  ]
);

// Email Campaigns - Send emails to user criteria at scheduled times
export const emailCampaigns = pgTable(
  "email_campaigns",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 255 }).notNull(),
    criteriaId: integer("criteria_id").references(() => userCriteria.id), // Target user criteria for this campaign (nullable for backward compatibility)
    targetSegmentId: integer("target_segment_id").references(
      () => userSegments.id
    ), // Optional target user segment for this campaign
    templateId: integer("template_id").references(() => emailTemplates.id), // Reference to internal email template
    sesTemplateName: varchar("ses_template_name", { length: 255 }), // AWS SES template name (for direct SES templates)
    channel: varchar("channel", { length: 20 }).notNull().default("email"), // email or sms
    smsTemplateId: integer("sms_template_id"), // Optional SMS template for SMS campaigns
    status: varchar("status", { length: 50 }).notNull().default("draft"), // draft, scheduled, sending, completed, cancelled
    throttlePerMin: integer("throttle_per_min").default(25000), // Rate limiting for email sends
    cronSchedule: varchar("cron_schedule", { length: 100 }), // Cron expression for recurring campaigns (e.g., "0 9 * * *" for daily at 9am)
    lastSentAt: timestamp("last_sent_at"), // Last time the campaign was sent (for recurring campaigns)
    sentAt: timestamp("sent_at"), // When the campaign was actually sent (for one-time campaigns)
    recipientCount: integer("recipient_count").default(0), // Number of users who received the email
    utm: jsonb("utm"), // UTM tracking parameters: {source, medium, campaign, term, content}
    description: text("description"),
    createdBy: integer("created_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_email_campaigns_criteria_id").on(table.criteriaId),
    index("idx_email_campaigns_target_segment_id").on(table.targetSegmentId),
    index("idx_email_campaigns_template_id").on(table.templateId),
    index("idx_email_campaigns_status").on(table.status),
    index("idx_email_campaigns_created_by").on(table.createdBy),
    index("idx_email_campaigns_created_at").on(table.createdAt),
    check(
      "check_email_campaigns_status",
      sql`${table.status} IN ('draft', 'scheduled', 'sending', 'completed', 'cancelled')`
    ),
  ]
);

// Junction table for multi-criteria campaign support
export const emailCampaignCriteria = pgTable(
  "email_campaign_criteria",
  {
    id: serial("id").primaryKey(),
    campaignId: integer("campaign_id")
      .references(() => emailCampaigns.id, { onDelete: "cascade" })
      .notNull(),
    criteriaId: integer("criteria_id")
      .references(() => userCriteria.id, { onDelete: "cascade" })
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_email_campaign_criteria_campaign_id").on(table.campaignId),
    index("idx_email_campaign_criteria_criteria_id").on(table.criteriaId),
    uniqueIndex("idx_email_campaign_criteria_unique").on(
      table.campaignId,
      table.criteriaId
    ),
  ]
);

export const emailCampaignRuns = pgTable(
  "email_campaign_runs",
  {
    id: serial("id").primaryKey(),
    campaignId: integer("campaign_id")
      .references(() => emailCampaigns.id, { onDelete: "cascade" })
      .notNull(),
    runType: varchar("run_type", { length: 50 }).notNull().default("manual"),
    status: varchar("status", { length: 50 }).notNull().default("in_progress"),
    scheduledAt: timestamp("scheduled_at").defaultNow().notNull(),
    startedAt: timestamp("started_at").defaultNow().notNull(),
    completedAt: timestamp("completed_at"),
    recipientCount: integer("recipient_count").default(0).notNull(),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_email_campaign_runs_campaign_id").on(table.campaignId),
    index("idx_email_campaign_runs_status").on(table.status),
    index("idx_email_campaign_runs_scheduled_at").on(table.scheduledAt),
    index("idx_email_campaign_runs_started_at").on(table.startedAt),
  ]
);

export const emailCampaignRunRecipients = pgTable(
  "email_campaign_run_recipients",
  {
    id: serial("id").primaryKey(),
    runId: integer("run_id")
      .references(() => emailCampaignRuns.id, { onDelete: "cascade" })
      .notNull(),
    userId: integer("user_id").references(() => users.id),
    email: varchar("email", { length: 255 }).notNull(),
    status: varchar("status", { length: 50 }).notNull().default("sent"),
    error: text("error"),
    sentAt: timestamp("sent_at").defaultNow().notNull(),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_email_campaign_run_recipients_run_id").on(table.runId),
    index("idx_email_campaign_run_recipients_user_id").on(table.userId),
    index("idx_email_campaign_run_recipients_status").on(table.status),
  ]
);

// User Criteria - Reusable user targeting/segmentation criteria for campaigns
export const userCriteria = pgTable(
  "user_criteria",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    criteriaType: varchar("criteria_type", { length: 100 }).notNull(), // new_users, inactive_users, specific_users, custom, bulk_import
    criteriaParams: jsonb("criteria_params").notNull(), // Parameters for the criteria: {days: 7} for inactive_users, {userIds: [1,2,3]} for specific_users
    updateFrequency: varchar("update_frequency", { length: 50 }).default(
      "manual"
    ), // manual, hourly, daily, weekly
    lastUpdatedAt: timestamp("last_updated_at"),
    matchingUserCount: integer("matching_user_count").default(0),
    isActive: boolean("is_active").default(true),
    createdBy: integer("created_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_user_criteria_type").on(table.criteriaType),
    index("idx_user_criteria_is_active").on(table.isActive),
    index("idx_user_criteria_created_by").on(table.createdBy),
    index("idx_user_criteria_created_at").on(table.createdAt),
    index("idx_user_criteria_update_frequency").on(table.updateFrequency),
    index("idx_user_criteria_last_updated").on(table.lastUpdatedAt),
  ]
);

// Bulk import uploads for user criteria
export const userCriteriaBulkImports = pgTable(
  "user_criteria_bulk_imports",
  {
    id: serial("id").primaryKey(),
    criteriaId: integer("criteria_id")
      .references(() => userCriteria.id, { onDelete: "cascade" })
      .notNull(),
    uploadedBy: integer("uploaded_by").references(() => users.id),
    originalFilename: varchar("original_filename", { length: 255 }).notNull(),
    totalRows: integer("total_rows").default(0).notNull(),
    validRows: integer("valid_rows").default(0).notNull(),
    invalidRows: integer("invalid_rows").default(0).notNull(),
    status: varchar("status", { length: 50 }).default("completed").notNull(), // completed, failed, processing
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow(),
    processedAt: timestamp("processed_at"),
  },
  (table) => [
    index("idx_user_criteria_bulk_imports_criteria_id").on(table.criteriaId),
    index("idx_user_criteria_bulk_imports_status").on(table.status),
    index("idx_user_criteria_bulk_imports_created_at").on(table.createdAt),
  ]
);

export const userCriteriaBulkImportContacts = pgTable(
  "user_criteria_bulk_import_contacts",
  {
    id: serial("id").primaryKey(),
    importId: integer("import_id")
      .references(() => userCriteriaBulkImports.id, { onDelete: "cascade" })
      .notNull(),
    email: varchar("email", { length: 255 }).notNull(),
    firstName: varchar("first_name", { length: 255 }).notNull(),
    lastName: varchar("last_name", { length: 255 }).notNull(),
    phone: varchar("phone", { length: 50 }),
    dateOfBirth: varchar("date_of_birth", { length: 50 }),
    gender: varchar("gender", { length: 50 }),
    idNumber: varchar("id_number", { length: 255 }),
    source: varchar("source", { length: 255 }),
    matchedUserId: integer("matched_user_id").references(() => users.id),
    rawData: jsonb("raw_data"),
    validationErrors: jsonb("validation_errors"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_bulk_import_contacts_import_id").on(table.importId),
    index("idx_bulk_import_contacts_email").on(table.email),
    index("idx_bulk_import_contacts_matched_user").on(table.matchedUserId),
    uniqueIndex("idx_bulk_import_contacts_unique_email_per_import").on(
      table.importId,
      table.email
    ),
  ]
);

export const userCriteriaBulkImportConversions = pgTable(
  "user_criteria_bulk_import_conversions",
  {
    id: serial("id").primaryKey(),
    criteriaId: integer("criteria_id")
      .references(() => userCriteria.id, { onDelete: "cascade" })
      .notNull(),
    importId: integer("import_id").references(() => userCriteriaBulkImports.id),
    email: varchar("email", { length: 255 }).notNull(),
    phone: varchar("phone", { length: 50 }),
    userId: integer("user_id").references(() => users.id),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_bulk_import_conversions_criteria_id").on(table.criteriaId),
    index("idx_bulk_import_conversions_import_id").on(table.importId),
    index("idx_bulk_import_conversions_user_id").on(table.userId),
  ]
);

// Campaign Steps - Multi-step email sequences with delays and branching
export const campaignSteps = pgTable(
  "campaign_steps",
  {
    id: serial("id").primaryKey(),
    campaignId: integer("campaign_id")
      .references(() => emailCampaigns.id, { onDelete: "cascade" })
      .notNull(),
    stepIndex: integer("step_index").notNull(), // Order of steps in sequence (0, 1, 2, ...)
    name: varchar("name", { length: 255 }).notNull(),
    delayMinutes: integer("delay_minutes").default(0), // Delay before sending this step
    templateId: integer("template_id")
      .references(() => emailTemplates.id)
      .notNull(),
    filter: jsonb("filter"), // Additional conditions for this step: {segment: "premium", tier: "advanced"}
    branchCondition: jsonb("branch_condition"), // Branching logic: {field: "tier", operator: "equals", value: "premium", trueStepIndex: 2, falseStepIndex: 3}
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_campaign_steps_campaign_id").on(table.campaignId),
    index("idx_campaign_steps_campaign_step").on(
      table.campaignId,
      table.stepIndex
    ),
    index("idx_campaign_steps_template_id").on(table.templateId),
    index("idx_campaign_steps_is_active").on(table.isActive),
  ]
);

// User Journeys - Track individual users progress through campaigns
export const userJourneys = pgTable(
  "user_journeys",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    campaignId: integer("campaign_id")
      .references(() => emailCampaigns.id, { onDelete: "cascade" })
      .notNull(),
    state: varchar("state", { length: 50 }).notNull().default("pending"), // pending, active, paused, completed, cancelled
    currentStepIndex: integer("current_step_index").default(0), // Current step in the journey
    context: jsonb("context"), // Journey context data: user data, trigger event data, etc.
    nextStepAt: timestamp("next_step_at"), // When next step should be executed
    enrollmentCycle: varchar("enrollment_cycle"), // Campaign cycle when user was enrolled (e.g., "2025-01-15")
    startedAt: timestamp("started_at").defaultNow(),
    lastEventAt: timestamp("last_event_at").defaultNow(),
    completedAt: timestamp("completed_at"),
    pausedAt: timestamp("paused_at"),
    cancelledAt: timestamp("cancelled_at"),
    cancelReason: varchar("cancel_reason"),
  },
  (table) => [
    index("idx_user_journeys_user_id").on(table.userId),
    index("idx_user_journeys_campaign_id").on(table.campaignId),
    index("idx_user_journeys_state").on(table.state),
    index("idx_user_journeys_user_campaign").on(table.userId, table.campaignId),
    index("idx_user_journeys_next_step_at").on(table.nextStepAt),
    index("idx_user_journeys_started_at").on(table.startedAt),
    index("idx_user_journeys_enrollment_cycle").on(table.enrollmentCycle),
    check(
      "check_user_journeys_state",
      sql`${table.state} IN ('pending', 'active', 'paused', 'completed', 'cancelled')`
    ),
  ]
);

// Email Sends - Individual email deliveries (matching actual database schema)
export const emailSends = pgTable(
  "email_sends",
  {
    id: serial("id").primaryKey(),
    campaignId: integer("campaign_id").references(() => emailCampaigns.id),
    journeyId: integer("journey_id"),
    templateId: integer("template_id").references(() => emailTemplates.id),
    userId: integer("user_id").references(() => users.id, {
      onDelete: "cascade",
    }), // CASCADE: Delete email_sends when user is deleted
    runId: integer("run_id").references(() => emailCampaignRuns.id, {
      onDelete: "cascade",
    }),
    sendAt: timestamp("send_at").defaultNow(),
    status: varchar("status", { length: 20 }).default("sent"),
    sendgridMessageId: varchar("sendgrid_message_id", { length: 255 }), // Legacy name - stores any provider's message ID
    provider: varchar("provider", { length: 50 }), // Which provider sent this email (ses, smtp, dev)
    error: text("error"), // Error message if send failed
    variables: jsonb("variables"),
    cost: numeric("cost", { precision: 10, scale: 6 }).default("0.0001"), // Cost per email in USD ($0.0001 = 1,000 emails = $0.10)
    createdAt: timestamp("created_at").defaultNow(),
    sentAt: timestamp("sent_at"),
    scheduledAt: timestamp("scheduled_at").defaultNow(),
  },
  (table) => [
    index("idx_email_sends_status").on(table.status),
    index("idx_email_sends_user_id").on(table.userId),
    index("idx_email_sends_campaign_id").on(table.campaignId),
    index("idx_email_sends_scheduled_at").on(table.scheduledAt),
  ]
);

// Email Events - Tracking email interactions (opens, clicks, bounces, etc.)
export const emailEvents = pgTable(
  "email_events",
  {
    id: serial("id").primaryKey(),
    sendId: integer("send_id")
      .references(() => emailSends.id, { onDelete: "cascade" })
      .notNull(),
    type: varchar("type", { length: 50 }).notNull(), // delivered, open, click, bounce, spam, unsubscribe
    occurredAt: timestamp("occurred_at").defaultNow(),
    userAgent: text("user_agent"), // User agents can be very long
    ipAddress: varchar("ip_address"),
    location: text("location"), // Geographic location if available
    clickUrl: text("click_url"), // For click events - URLs can be very long with tracking params
    bounceReason: text("bounce_reason"), // For bounce events - bounce messages can be verbose
    metadata: jsonb("metadata"), // Additional event data from webhook
    providerEventId: text("provider_event_id").unique(), // Prevent duplicate events - constructed from messageId + timestamp + link
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_email_events_send_id").on(table.sendId),
    index("idx_email_events_type").on(table.type),
    index("idx_email_events_occurred_at").on(table.occurredAt),
    index("idx_email_events_provider_event_id").on(table.providerEventId),
    // Composite indexes for analytics
    index("idx_email_events_send_type").on(table.sendId, table.type),
    index("idx_email_events_type_occurred").on(table.type, table.occurredAt),
    check(
      "check_email_events_type",
      sql`${table.type} IN ('delivered', 'open', 'click', 'bounce', 'spam', 'unsubscribe')`
    ),
  ]
);

// User Journey Triggers - Define reusable trigger conditions for journey automation
export const userJourneyTriggers = pgTable(
  "user_journey_triggers",
  {
    id: serial("id").primaryKey(),
    key: varchar("key", { length: 255 }).notNull().unique(), // Unique identifier for the trigger
    name: varchar("name", { length: 255 }).notNull(), // Human-readable name
    description: text("description"), // Description of what the trigger does
    source: varchar("source", { length: 20 }).notNull(), // 'crm', 'webhook', 'time'
    sourceEventTypes: jsonb("source_event_types").notNull(), // Array of event types this trigger listens for
    payloadSchema: jsonb("payload_schema"), // JSON schema for validating trigger payloads
    defaultFilter: jsonb("default_filter"), // Default filter conditions
    status: varchar("status", { length: 20 }).notNull().default("active"), // 'active', 'disabled'
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_user_journey_triggers_key").on(table.key),
    index("idx_user_journey_triggers_source").on(table.source),
    index("idx_user_journey_triggers_status").on(table.status),
    index("idx_user_journey_triggers_created_at").on(table.createdAt),
    check(
      "check_user_journey_triggers_source",
      sql`${table.source} IN ('crm', 'webhook', 'time')`
    ),
    check(
      "check_user_journey_triggers_status",
      sql`${table.status} IN ('active', 'disabled')`
    ),
  ]
);

// ============================================================================
// USER-CREATED JOURNEY BUILDER SYSTEM TABLES
// ============================================================================

// User Journey Templates - Template definitions for reusable journey patterns
export const userJourneyTemplates = pgTable(
  "user_journey_templates",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    templateType: varchar("template_type", { length: 50 })
      .notNull()
      .default("custom"), // onboarding, engagement, retention, marketing, custom
    category: varchar("category", { length: 100 }), // welcome_series, product_updates, re_engagement, etc.
    triggerEvent: varchar("trigger_event", { length: 100 }), // user_registered, profile_completed, inactivity_detected, etc.
    triggerSettings: jsonb("trigger_settings"), // Additional trigger configuration
    estimatedDuration: integer("estimated_duration"), // Estimated completion time in minutes
    targetAudience: varchar("target_audience", { length: 100 }), // new_users, premium_users, all_users, etc.
    goals: text("goals")
      .array()
      .default(sql`ARRAY[]::text[]`), // ["increase_engagement", "improve_retention"]
    isPublic: boolean("is_public").default(false), // Can other users see/use this template
    isSystemTemplate: boolean("is_system_template").default(false), // Built-in vs user-created
    version: varchar("version", { length: 20 }).default("1.0"),
    status: varchar("status", { length: 20 }).notNull().default("draft"), // draft, active, archived
    tags: text("tags")
      .array()
      .default(sql`ARRAY[]::text[]`), // For categorization and search
    metadata: jsonb("metadata"), // Additional template settings and configuration
    createdBy: integer("created_by")
      .references(() => users.id)
      .notNull(),
    lastModifiedBy: integer("last_modified_by").references(() => users.id),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_user_journey_templates_type").on(table.templateType),
    index("idx_user_journey_templates_category").on(table.category),
    index("idx_user_journey_templates_trigger").on(table.triggerEvent),
    index("idx_user_journey_templates_status").on(table.status),
    index("idx_user_journey_templates_public").on(table.isPublic),
    index("idx_user_journey_templates_system").on(table.isSystemTemplate),
    index("idx_user_journey_templates_created_by").on(table.createdBy),
    index("idx_user_journey_templates_created_at").on(table.createdAt),
  ]
);

// Custom Journeys - User-created journey instances (can be from templates or from scratch)
export const customJourneys = pgTable(
  "custom_journeys",
  {
    id: serial("id").primaryKey(),
    templateId: integer("template_id").references(
      () => userJourneyTemplates.id,
      { onDelete: "set null" }
    ), // Optional - can be created from template
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    journeyType: varchar("journey_type", { length: 50 })
      .notNull()
      .default("custom"), // automated, triggered, manual, custom
    triggerEvent: varchar("trigger_event", { length: 100 }), // What triggers this journey
    triggerConditions: jsonb("trigger_conditions"), // Additional conditions for triggering
    targetSegmentId: integer("target_segment_id").references(
      () => userSegments.id,
      { onDelete: "set null" }
    ), // Target audience segment
    schedulingSettings: jsonb("scheduling_settings"), // When and how often to run (rate limiting, weekend sending)
    sesTemplateName: varchar("ses_template_name", { length: 255 }), // AWS SES template name to use for email
    status: varchar("status", { length: 20 }).notNull().default("draft"), // draft, active, paused, completed, archived
    isTemplate: boolean("is_template").default(false), // Can this journey be used as a template
    version: varchar("version", { length: 20 }).default("1.0"),
    parentJourneyId: integer("parent_journey_id").references(
      () => customJourneys.id,
      { onDelete: "set null" }
    ), // For versioning/copying
    totalSteps: integer("total_steps").default(0), // Cached count of steps
    estimatedDuration: integer("estimated_duration"), // Estimated completion time in minutes
    metrics: jsonb("metrics"), // Journey performance metrics
    settings: jsonb("settings"), // Additional journey configuration
    tags: text("tags")
      .array()
      .default(sql`ARRAY[]::text[]`),
    metadata: jsonb("metadata"),
    createdBy: integer("created_by")
      .references(() => users.id)
      .notNull(),
    lastModifiedBy: integer("last_modified_by").references(() => users.id),
    publishedAt: timestamp("published_at"), // When journey was activated
    archivedAt: timestamp("archived_at"), // When journey was archived
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_custom_journeys_template_id").on(table.templateId),
    index("idx_custom_journeys_type").on(table.journeyType),
    index("idx_custom_journeys_trigger").on(table.triggerEvent),
    index("idx_custom_journeys_status").on(table.status),
    index("idx_custom_journeys_segment").on(table.targetSegmentId),
    index("idx_custom_journeys_created_by").on(table.createdBy),
    index("idx_custom_journeys_is_template").on(table.isTemplate),
    index("idx_custom_journeys_published_at").on(table.publishedAt),
    index("idx_custom_journeys_created_at").on(table.createdAt),
  ]
);

// Custom Email Templates - User-created email templates specifically for journeys
export const customEmailTemplates = pgTable(
  "custom_email_templates",
  {
    id: serial("id").primaryKey(),
    journeyId: integer("journey_id").references(() => customJourneys.id, {
      onDelete: "cascade",
    }), // Associated journey (optional)
    name: varchar("name", { length: 255 }).notNull(),
    subject: varchar("subject", { length: 255 }).notNull(),
    bodyHtml: text("body_html").notNull(),
    bodyText: text("body_text"), // Plain text version
    previewText: varchar("preview_text", { length: 250 }), // Email preview text
    templateType: varchar("template_type", { length: 50 }).default("journey"), // journey, standalone, system
    category: varchar("category", { length: 100 }).default("custom"), // welcome, follow_up, reminder, promotional, etc.
    variables: jsonb("variables"), // Template variables: [{"name": "firstName", "type": "string", "default": "", "required": true}]
    designSettings: jsonb("design_settings"), // Colors, fonts, layout preferences
    isShared: boolean("is_shared").default(false), // Can other users use this template
    isApproved: boolean("is_approved").default(true), // Admin approval for shared templates
    usage_count: integer("usage_count").default(0), // How many times template has been used
    parentTemplateId: integer("parent_template_id").references(
      () => customEmailTemplates.id,
      { onDelete: "set null" }
    ), // For template inheritance
    version: varchar("version", { length: 20 }).default("1.0"),
    tags: text("tags")
      .array()
      .default(sql`ARRAY[]::text[]`),
    status: varchar("status", { length: 20 }).notNull().default("active"), // draft, active, archived
    metadata: jsonb("metadata"),
    createdBy: integer("created_by")
      .references(() => users.id)
      .notNull(),
    lastModifiedBy: integer("last_modified_by").references(() => users.id),
    approvedBy: integer("approved_by").references(() => users.id), // Who approved shared template
    approvedAt: timestamp("approved_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_custom_email_templates_journey_id").on(table.journeyId),
    index("idx_custom_email_templates_type").on(table.templateType),
    index("idx_custom_email_templates_category").on(table.category),
    index("idx_custom_email_templates_shared").on(table.isShared),
    index("idx_custom_email_templates_approved").on(table.isApproved),
    index("idx_custom_email_templates_status").on(table.status),
    index("idx_custom_email_templates_created_by").on(table.createdBy),
    index("idx_custom_email_templates_created_at").on(table.createdAt),
  ]
);

// Journey Steps - Individual steps in user-created journeys
export const journeySteps = pgTable(
  "journey_steps",
  {
    id: serial("id").primaryKey(),
    journeyId: integer("journey_id")
      .references(() => customJourneys.id, { onDelete: "cascade" })
      .notNull(),
    parentStepId: integer("parent_step_id").references(() => journeySteps.id, {
      onDelete: "cascade",
    }), // For nested/conditional steps
    stepType: varchar("step_type", { length: 50 }).notNull(), // email, delay, condition, action, split_test, webhook
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    stepIndex: integer("step_index").notNull(), // Order within the journey
    emailTemplateId: integer("email_template_id").references(
      () => customEmailTemplates.id,
      { onDelete: "set null" }
    ), // For email steps
    delaySettings: jsonb("delay_settings"), // For delay steps: {amount: 2, unit: "hours", businessHours: true}
    actionSettings: jsonb("action_settings"), // For action steps: {type: "add_tag", value: "engaged_user"}
    webhookSettings: jsonb("webhook_settings"), // For webhook steps: {url: "", method: "POST", headers: {}}
    splitTestSettings: jsonb("split_test_settings"), // For A/B testing: {variants: [stepIdA, stepIdB], traffic: [50, 50]}
    isActive: boolean("is_active").default(true),
    isConditional: boolean("is_conditional").default(false), // Has branching logic
    position: jsonb("position"), // Visual editor position: {x: 100, y: 200}
    config: jsonb("config"), // Step-specific configuration
    metadata: jsonb("metadata"),
    createdBy: integer("created_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_journey_steps_journey_id").on(table.journeyId),
    index("idx_journey_steps_parent_id").on(table.parentStepId),
    index("idx_journey_steps_type").on(table.stepType),
    index("idx_journey_steps_index").on(table.stepIndex),
    index("idx_journey_steps_email_template").on(table.emailTemplateId),
    index("idx_journey_steps_active").on(table.isActive),
    index("idx_journey_steps_conditional").on(table.isConditional),
    index("idx_journey_steps_created_by").on(table.createdBy),
    // Composite index for journey step ordering
    index("idx_journey_steps_journey_order").on(
      table.journeyId,
      table.stepIndex
    ),
  ]
);

// Journey Conditions - Conditional logic for journey branching and step execution
export const journeyConditions = pgTable(
  "journey_conditions",
  {
    id: serial("id").primaryKey(),
    stepId: integer("step_id")
      .references(() => journeySteps.id, { onDelete: "cascade" })
      .notNull(),
    conditionType: varchar("condition_type", { length: 50 }).notNull(), // user_property, user_action, time_based, email_engagement, custom
    field: varchar("field", { length: 100 }).notNull(), // User field or event to check
    operator: varchar("operator", { length: 20 }).notNull(), // equals, not_equals, greater_than, less_than, contains, exists, in_list
    value: text("value"), // Value to compare against (can be JSON for complex values)
    logicalOperator: varchar("logical_operator", { length: 10 }).default("AND"), // AND, OR for combining conditions
    conditionOrder: integer("condition_order").default(0), // Order for evaluation
    trueStepId: integer("true_step_id").references(() => journeySteps.id, {
      onDelete: "set null",
    }), // Step to go to if condition is true
    falseStepId: integer("false_step_id").references(() => journeySteps.id, {
      onDelete: "set null",
    }), // Step to go to if condition is false
    isActive: boolean("is_active").default(true),
    metadata: jsonb("metadata"), // Additional condition configuration
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_journey_conditions_step_id").on(table.stepId),
    index("idx_journey_conditions_type").on(table.conditionType),
    index("idx_journey_conditions_field").on(table.field),
    index("idx_journey_conditions_operator").on(table.operator),
    index("idx_journey_conditions_true_step").on(table.trueStepId),
    index("idx_journey_conditions_false_step").on(table.falseStepId),
    index("idx_journey_conditions_active").on(table.isActive),
    // Composite index for condition evaluation
    index("idx_journey_conditions_step_order").on(
      table.stepId,
      table.conditionOrder
    ),
  ]
);

// Journey Email Logs - Track email sends from automated journeys
export const journeyEmailLogs = pgTable(
  "journey_email_logs",
  {
    id: serial("id").primaryKey(),
    journeyId: integer("journey_id")
      .references(() => customJourneys.id, { onDelete: "cascade" })
      .notNull(),
    stepId: integer("step_id")
      .references(() => journeySteps.id, { onDelete: "cascade" })
      .notNull(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    triggerEvent: varchar("trigger_event", { length: 100 }).notNull(), // Event that triggered this send (e.g., "user:registered")
    emailTemplateId: integer("email_template_id").references(
      () => customEmailTemplates.id,
      { onDelete: "set null" }
    ),
    emailSendId: integer("email_send_id").references(() => emailSends.id, {
      onDelete: "set null",
    }), // Link to actual email send record
    status: varchar("status", { length: 20 }).notNull().default("sent"), // sent, failed, skipped, scheduled
    sentAt: timestamp("sent_at").defaultNow(),
    failureReason: text("failure_reason"), // Error message if failed
    metadata: jsonb("metadata"), // Additional data (user data at time of send, etc.)
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_journey_email_logs_journey_id").on(table.journeyId),
    index("idx_journey_email_logs_step_id").on(table.stepId),
    index("idx_journey_email_logs_user_id").on(table.userId),
    index("idx_journey_email_logs_trigger_event").on(table.triggerEvent),
    index("idx_journey_email_logs_status").on(table.status),
    index("idx_journey_email_logs_sent_at").on(table.sentAt),
    index("idx_journey_email_logs_email_send_id").on(table.emailSendId),
    // Composite indexes for analytics
    index("idx_journey_email_logs_journey_status").on(
      table.journeyId,
      table.status
    ),
    index("idx_journey_email_logs_user_journey").on(
      table.userId,
      table.journeyId
    ),
  ]
);

// Engagement Trigger Tracking - Track when engagement triggers fire for rate limiting
export const engagementTriggerTracking = pgTable(
  "engagement_trigger_tracking",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    triggerType: varchar("trigger_type", { length: 50 }).notNull(), // 'unanswered_questions', 'missing_competitions'
    lastTriggeredAt: timestamp("last_triggered_at").defaultNow().notNull(),
    metadata: jsonb("metadata"), // Store competition_id, entry_id, missing_competition_ids, etc.
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_engagement_trigger_tracking_user_trigger").on(
      table.userId,
      table.triggerType
    ),
    index("idx_engagement_trigger_tracking_triggered_at").on(
      table.lastTriggeredAt
    ),
    index("idx_engagement_trigger_tracking_type_time").on(
      table.triggerType,
      table.lastTriggeredAt
    ),
    check(
      "check_engagement_trigger_type",
      sql`${table.triggerType} IN (
        'unanswered_questions',
        'missing_competitions',
        '24_hour_betfred_unverified',
        'inactive_3_days',
        'inactive_7_days',
        'inactive_14_days',
        'inactive_21_days',
        'inactive_28_days',
        'entered_all_competitions_week'
      )`
    ),
  ]
);

// Email Contact Opt-Outs - track opt-outs for contacts without user accounts
export const emailContactOptOuts = pgTable(
  "email_contact_opt_outs",
  {
    id: serial("id").primaryKey(),
    email: varchar("email", { length: 255 }).notNull(),
    criteriaId: integer("criteria_id").references(() => userCriteria.id, {
      onDelete: "set null",
    }),
    reason: text("reason"),
    source: varchar("source", { length: 50 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_email_contact_opt_outs_email").on(sql`LOWER(${table.email})`),
    index("idx_email_contact_opt_outs_criteria").on(table.criteriaId),
  ]
);

// Email Suppression List - permanently suppress emails due to bounces/complaints
export const emailSuppressionList = pgTable(
  "email_suppression_list",
  {
    id: serial("id").primaryKey(),
    email: varchar("email", { length: 255 }).notNull(),
    reason: varchar("reason", { length: 50 }).notNull(), // 'hard_bounce', 'soft_bounce', 'complaint', 'manual'
    bounceType: varchar("bounce_type", { length: 50 }), // 'Permanent', 'Transient', 'Undetermined'
    bounceSubType: varchar("bounce_sub_type", { length: 100 }), // e.g., 'General', 'NoEmail', 'Suppressed'
    diagnosticCode: text("diagnostic_code"), // SMTP diagnostic code
    feedbackType: varchar("feedback_type", { length: 50 }), // For complaints: 'abuse', 'auth-failure', etc.
    sourceMessageId: varchar("source_message_id", { length: 255 }), // Original SES message ID that caused suppression
    userId: integer("user_id").references(() => users.id, {
      onDelete: "set null",
    }), // Link to user if exists
    bounceCount: integer("bounce_count").default(0),
    metadata: jsonb("metadata"), // Additional data from SES
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    // Unique constraint on raw email for upserts.
    // Webhook handlers normalize to lowercase before inserting, so this
    // still behaves case-insensitively in practice while satisfying
    // Postgres ON CONFLICT requirements.
    unique("email_suppression_list_email_unique").on(table.email),
    index("idx_email_suppression_email").on(sql`LOWER(${table.email})`),
    index("idx_email_suppression_reason").on(table.reason),
    index("idx_email_suppression_user_id").on(table.userId),
  ]
);

// Weekly Email Quota - limit marketing emails per user per custom week
export const userEmailWeeklyQuota = pgTable(
  "user_email_weekly_quota",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    weekStart: timestamp("week_start").notNull(),
    emailCount: integer("email_count").notNull().default(0),
  },
  (table) => [
    uniqueIndex("uniq_user_email_weekly_quota").on(
      table.userId,
      table.weekStart
    ),
    index("idx_user_email_weekly_quota_user_week").on(
      table.userId,
      table.weekStart
    ),
  ]
);

// In-App Messages - per-user notifications shown inside the app
export const inAppMessages = pgTable(
  "in_app_messages",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    title: varchar("title", { length: 255 }).notNull(),
    body: text("body").notNull(),
    messageType: varchar("message_type", { length: 50 }).default("info"),
    isRead: boolean("is_read").default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    readAt: timestamp("read_at"),
  },
  (table) => [
    index("idx_in_app_messages_user_id").on(
      table.userId,
      table.isRead,
      table.createdAt
    ),
  ]
);

// Campaign Notified Users - tracks which users have been notified by special campaigns
export const campaignNotifiedUsers = pgTable(
  "campaign_notified_users",
  {
    id: serial("id").primaryKey(),
    campaignId: varchar("campaign_id", { length: 100 }).notNull(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    notifiedAt: timestamp("notified_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_campaign_notified_users_campaign_id").on(table.campaignId),
    index("idx_campaign_notified_users_user_id").on(table.userId),
    unique("uniq_campaign_notified_user").on(table.campaignId, table.userId),
  ]
);

// Relations
export const settingsRelations = relations(settings, ({}) => ({}));

export const rolesRelations = relations(roles, ({ many }) => ({
  userRoles: many(userRoles),
}));

export const userRolesRelations = relations(userRoles, ({ one }) => ({
  user: one(users, {
    fields: [userRoles.userId],
    references: [users.id],
  }),
  role: one(roles, {
    fields: [userRoles.roleId],
    references: [roles.id],
  }),
}));

export const bugsRelations = relations(bugs, ({ one, many }) => ({
  assignedTo: one(users, {
    fields: [bugs.assignedTo],
    references: [users.id],
  }),
  reportedBy: one(users, {
    fields: [bugs.reportedBy],
    references: [users.id],
  }),
  attachments: many(bugAttachments),
  comments: many(bugComments),
}));

export const bugAttachmentsRelations = relations(bugAttachments, ({ one }) => ({
  bug: one(bugs, {
    fields: [bugAttachments.bugId],
    references: [bugs.id],
  }),
}));

export const testCasesRelations = relations(testCases, ({ one, many }) => ({
  createdBy: one(users, {
    fields: [testCases.createdBy],
    references: [users.id],
  }),
  executions: many(testExecutions),
}));

export const testRunsRelations = relations(testRuns, ({ one, many }) => ({
  executedBy: one(users, {
    fields: [testRuns.executedBy],
    references: [users.id],
  }),
  executions: many(testExecutions),
}));

// Social Media Relations
export const socialChannelsRelations = relations(
  socialChannels,
  ({ one, many }) => ({
    createdBy: one(users, {
      fields: [socialChannels.createdBy],
      references: [users.id],
    }),
    analytics: many(socialAnalytics),
  })
);

export const socialPostsRelations = relations(socialPosts, ({ one, many }) => ({
  createdBy: one(users, {
    fields: [socialPosts.createdBy],
    references: [users.id],
  }),
  analytics: many(socialAnalytics),
}));

export const socialAnalyticsRelations = relations(
  socialAnalytics,
  ({ one }) => ({
    channel: one(socialChannels, {
      fields: [socialAnalytics.channelId],
      references: [socialChannels.id],
    }),
    post: one(socialPosts, {
      fields: [socialAnalytics.postId],
      references: [socialPosts.id],
    }),
  })
);

export const socialTemplatesRelations = relations(
  socialTemplates,
  ({ one }) => ({
    createdBy: one(users, {
      fields: [socialTemplates.createdBy],
      references: [users.id],
    }),
  })
);

// ============================================================================
// TRIGGER MANAGEMENT SCHEMAS
// ============================================================================

// Schemas moved to end of file

// Types moved to end of file

// Spinner relations
export const spinnerEntriesRelations = relations(spinnerEntries, ({ one }) => ({
  user: one(users, {
    fields: [spinnerEntries.userId],
    references: [users.id],
  }),
}));

// Tables moved before schemas

// Communications relations
export const communicationsRelations = relations(communications, ({ one }) => ({
  competition: one(competitions, {
    fields: [communications.competitionId],
    references: [competitions.id],
  }),
  winner: one(winners, {
    fields: [communications.winnerId],
    references: [winners.id],
  }),
  creator: one(users, {
    fields: [communications.createdBy],
    references: [users.id],
  }),
}));

// Tables moved before schemas

// Challenge Relations
export const challengesRelations = relations(challenges, ({ one, many }) => ({
  creator: one(users, {
    fields: [challenges.createdBy],
    references: [users.id],
  }),
  userProgress: many(userChallengeProgress),
  actions: many(challengeActions),
  referrals: many(challengeReferrals),
  winners: many(challengeWinners),
  leaderboardCache: one(leaderboardCache, {
    fields: [challenges.id],
    references: [leaderboardCache.challengeId],
  }),
}));

export const userChallengeProgressRelations = relations(
  userChallengeProgress,
  ({ one }) => ({
    user: one(users, {
      fields: [userChallengeProgress.userId],
      references: [users.id],
    }),
    challenge: one(challenges, {
      fields: [userChallengeProgress.challengeId],
      references: [challenges.id],
    }),
  })
);

export const challengeActionsRelations = relations(
  challengeActions,
  ({ one }) => ({
    user: one(users, {
      fields: [challengeActions.userId],
      references: [users.id],
    }),
    challenge: one(challenges, {
      fields: [challengeActions.challengeId],
      references: [challenges.id],
    }),
  })
);

export const challengeReferralsRelations = relations(
  challengeReferrals,
  ({ one }) => ({
    challenge: one(challenges, {
      fields: [challengeReferrals.challengeId],
      references: [challenges.id],
    }),
    referrer: one(users, {
      fields: [challengeReferrals.referrerId],
      references: [users.id],
      relationName: "challenge_referrer",
    }),
    referredUser: one(users, {
      fields: [challengeReferrals.referredUserId],
      references: [users.id],
      relationName: "challenge_referred",
    }),
  })
);

export const challengeWinnersRelations = relations(
  challengeWinners,
  ({ one }) => ({
    challenge: one(challenges, {
      fields: [challengeWinners.challengeId],
      references: [challenges.id],
    }),
    user: one(users, {
      fields: [challengeWinners.userId],
      references: [users.id],
    }),
  })
);

export const leaderboardCacheRelations = relations(
  leaderboardCache,
  ({ one }) => ({
    challenge: one(challenges, {
      fields: [leaderboardCache.challengeId],
      references: [challenges.id],
    }),
  })
);

// CRM Email Marketing Relations
export const emailTemplatesRelations = relations(
  emailTemplates,
  ({ one, many }) => ({
    createdBy: one(users, {
      fields: [emailTemplates.createdBy],
      references: [users.id],
    }),
    campaigns: many(emailCampaigns),
    campaignSteps: many(campaignSteps),
    emailSends: many(emailSends),
  })
);

export const emailCampaignsRelations = relations(
  emailCampaigns,
  ({ one, many }) => ({
    createdBy: one(users, {
      fields: [emailCampaigns.createdBy],
      references: [users.id],
    }),
    criteria: one(userCriteria, {
      fields: [emailCampaigns.criteriaId],
      references: [userCriteria.id],
    }),
    targetSegment: one(userSegments, {
      fields: [emailCampaigns.targetSegmentId],
      references: [userSegments.id],
    }),
    template: one(emailTemplates, {
      fields: [emailCampaigns.templateId],
      references: [emailTemplates.id],
    }),
    steps: many(campaignSteps),
    userJourneys: many(userJourneys),
    emailSends: many(emailSends),
    campaignCriteria: many(emailCampaignCriteria),
  })
);

export const emailCampaignCriteriaRelations = relations(
  emailCampaignCriteria,
  ({ one }) => ({
    campaign: one(emailCampaigns, {
      fields: [emailCampaignCriteria.campaignId],
      references: [emailCampaigns.id],
    }),
    criteria: one(userCriteria, {
      fields: [emailCampaignCriteria.criteriaId],
      references: [userCriteria.id],
    }),
  })
);

export const emailCampaignRunsRelations = relations(
  emailCampaignRuns,
  ({ one, many }) => ({
    campaign: one(emailCampaigns, {
      fields: [emailCampaignRuns.campaignId],
      references: [emailCampaigns.id],
    }),
    recipients: many(emailCampaignRunRecipients),
  })
);

export const emailCampaignRunRecipientsRelations = relations(
  emailCampaignRunRecipients,
  ({ one }) => ({
    run: one(emailCampaignRuns, {
      fields: [emailCampaignRunRecipients.runId],
      references: [emailCampaignRuns.id],
    }),
    user: one(users, {
      fields: [emailCampaignRunRecipients.userId],
      references: [users.id],
      relationName: "recipient_user",
    }),
  })
);

export const campaignStepsRelations = relations(
  campaignSteps,
  ({ one, many }) => ({
    campaign: one(emailCampaigns, {
      fields: [campaignSteps.campaignId],
      references: [emailCampaigns.id],
    }),
    template: one(emailTemplates, {
      fields: [campaignSteps.templateId],
      references: [emailTemplates.id],
    }),
    emailSends: many(emailSends),
  })
);

export const userJourneysRelations = relations(
  userJourneys,
  ({ one, many }) => ({
    user: one(users, {
      fields: [userJourneys.userId],
      references: [users.id],
    }),
    campaign: one(emailCampaigns, {
      fields: [userJourneys.campaignId],
      references: [emailCampaigns.id],
    }),
    emailSends: many(emailSends),
  })
);

export const emailSendsRelations = relations(emailSends, ({ one, many }) => ({
  user: one(users, {
    fields: [emailSends.userId],
    references: [users.id],
  }),
  journey: one(userJourneys, {
    fields: [emailSends.journeyId],
    references: [userJourneys.id],
  }),
  campaign: one(emailCampaigns, {
    fields: [emailSends.campaignId],
    references: [emailCampaigns.id],
  }),
  template: one(emailTemplates, {
    fields: [emailSends.templateId],
    references: [emailTemplates.id],
  }),
  run: one(emailCampaignRuns, {
    fields: [emailSends.runId],
    references: [emailCampaignRuns.id],
  }),
  events: many(emailEvents),
}));

export const emailEventsRelations = relations(emailEvents, ({ one }) => ({
  send: one(emailSends, {
    fields: [emailEvents.sendId],
    references: [emailSends.id],
  }),
}));

// User-Created Journey Builder Relations
export const userJourneyTemplatesRelations = relations(
  userJourneyTemplates,
  ({ one, many }) => ({
    createdBy: one(users, {
      fields: [userJourneyTemplates.createdBy],
      references: [users.id],
    }),
    lastModifiedBy: one(users, {
      fields: [userJourneyTemplates.lastModifiedBy],
      references: [users.id],
      relationName: "template_modified_by",
    }),
    customJourneys: many(customJourneys),
  })
);

export const customJourneysRelations = relations(
  customJourneys,
  ({ one, many }) => ({
    template: one(userJourneyTemplates, {
      fields: [customJourneys.templateId],
      references: [userJourneyTemplates.id],
    }),
    targetSegment: one(userSegments, {
      fields: [customJourneys.targetSegmentId],
      references: [userSegments.id],
    }),
    parentJourney: one(customJourneys, {
      fields: [customJourneys.parentJourneyId],
      references: [customJourneys.id],
      relationName: "journey_parent",
    }),
    childJourneys: many(customJourneys, {
      relationName: "journey_parent",
    }),
    createdBy: one(users, {
      fields: [customJourneys.createdBy],
      references: [users.id],
    }),
    lastModifiedBy: one(users, {
      fields: [customJourneys.lastModifiedBy],
      references: [users.id],
      relationName: "journey_modified_by",
    }),
    customEmailTemplates: many(customEmailTemplates),
    journeySteps: many(journeySteps),
  })
);

export const customEmailTemplatesRelations = relations(
  customEmailTemplates,
  ({ one, many }) => ({
    journey: one(customJourneys, {
      fields: [customEmailTemplates.journeyId],
      references: [customJourneys.id],
    }),
    parentTemplate: one(customEmailTemplates, {
      fields: [customEmailTemplates.parentTemplateId],
      references: [customEmailTemplates.id],
      relationName: "template_parent",
    }),
    childTemplates: many(customEmailTemplates, {
      relationName: "template_parent",
    }),
    createdBy: one(users, {
      fields: [customEmailTemplates.createdBy],
      references: [users.id],
    }),
    lastModifiedBy: one(users, {
      fields: [customEmailTemplates.lastModifiedBy],
      references: [users.id],
      relationName: "email_template_modified_by",
    }),
    approvedBy: one(users, {
      fields: [customEmailTemplates.approvedBy],
      references: [users.id],
      relationName: "email_template_approved_by",
    }),
    journeySteps: many(journeySteps),
  })
);

export const journeyStepsRelations = relations(
  journeySteps,
  ({ one, many }) => ({
    journey: one(customJourneys, {
      fields: [journeySteps.journeyId],
      references: [customJourneys.id],
    }),
    parentStep: one(journeySteps, {
      fields: [journeySteps.parentStepId],
      references: [journeySteps.id],
      relationName: "step_parent",
    }),
    childSteps: many(journeySteps, {
      relationName: "step_parent",
    }),
    emailTemplate: one(customEmailTemplates, {
      fields: [journeySteps.emailTemplateId],
      references: [customEmailTemplates.id],
    }),
    createdBy: one(users, {
      fields: [journeySteps.createdBy],
      references: [users.id],
    }),
    conditions: many(journeyConditions),
    trueConditions: many(journeyConditions, {
      relationName: "condition_true_step",
    }),
    falseConditions: many(journeyConditions, {
      relationName: "condition_false_step",
    }),
  })
);

export const journeyConditionsRelations = relations(
  journeyConditions,
  ({ one }) => ({
    step: one(journeySteps, {
      fields: [journeyConditions.stepId],
      references: [journeySteps.id],
    }),
    trueStep: one(journeySteps, {
      fields: [journeyConditions.trueStepId],
      references: [journeySteps.id],
      relationName: "condition_true_step",
    }),
    falseStep: one(journeySteps, {
      fields: [journeyConditions.falseStepId],
      references: [journeySteps.id],
      relationName: "condition_false_step",
    }),
  })
);

export const journeyEmailLogsRelations = relations(
  journeyEmailLogs,
  ({ one }) => ({
    journey: one(customJourneys, {
      fields: [journeyEmailLogs.journeyId],
      references: [customJourneys.id],
    }),
    step: one(journeySteps, {
      fields: [journeyEmailLogs.stepId],
      references: [journeySteps.id],
    }),
    user: one(users, {
      fields: [journeyEmailLogs.userId],
      references: [users.id],
    }),
    emailTemplate: one(customEmailTemplates, {
      fields: [journeyEmailLogs.emailTemplateId],
      references: [customEmailTemplates.id],
    }),
    emailSend: one(emailSends, {
      fields: [journeyEmailLogs.emailSendId],
      references: [emailSends.id],
    }),
  })
);

// ===== SCHEMAS =====
// All insert schemas for database operations

// Core schemas
export const insertUserSchema = z.object({
  username: z.string().min(1).max(50),
  email: z.string().email(),
  password: z.string().min(1),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  profileImageUrl: z.string().optional(),
  phone: z.string().optional(),
  dateOfBirth: z.string().optional(),
  idNumber: z.string().optional(),
  gender: z.string().optional(),
  location: z.string().optional(),
  interests: z.string().optional(),
  socialHandles: z.any().optional(),
  consentGiven: z.boolean().optional(),
  marketingConsent: z.boolean().optional(),
  termsAccepted: z.boolean().optional(),
  userStatus: z.string().optional(),
  referralCode: z.string().optional(),
  referredBy: z.number().optional(),
  gamifiedName: z.string().optional(),
  shareTier: z.string().optional(),
  tierPoints: z.number().optional(),
  profileCompletion: z.number().optional(),
  currentTier: z.string().optional(),
  profileConsentGiven: z.boolean().optional(),
  lastProfileUpdate: z.string().optional(),
  segmentId: z.number().optional(),
  segmentScore: z.string().optional(),
  profileMetadata: z.any().optional(),
  platform: z.string().optional(),
  source: z.string().optional(),
});

export const updateUserProfileSchema = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    username: z.string().optional(),
    email: z.string().email().optional(),
    profileImageUrl: z.string().optional(),
    phone: z.string().optional(),
    dateOfBirth: z.string().optional(),
    gender: z.string().optional(),
    location: z.string().optional(),
    interests: z.string().optional(),
    socialHandles: z.any().optional(),
    consentGiven: z.boolean().optional(),
    marketingConsent: z.boolean().optional(),
    termsAccepted: z.boolean().optional(),
    userStatus: z.string().optional(),
    referredBy: z.number().optional(),
    gamifiedName: z.string().optional(),
    shareTier: z.string().optional(),
    tierPoints: z.number().optional(),
    profileCompletion: z.number().optional(),
    currentTier: z.string().optional(),
    profileConsentGiven: z.boolean().optional(),
    idNumber: z.string().optional(),
    lastProfileUpdate: z.string().optional(),
    segmentId: z.number().optional(),
    segmentScore: z.string().optional(),
    profileMetadata: z.any().optional(),
    hasCustomizedUsername: z.boolean().optional(),
    source: z.string().optional(),
  })
  .partial();

export const insertCompetitionSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  prize: z.string().optional(),
  prize1: z.string().optional(),
  prize2: z.string().optional(),
  prize3: z.string().optional(),
  prizeValue: z.number().optional(),
  startDate: z.string(),
  startTime: z.string().optional(),
  endDate: z.string().optional(),
  endTime: z.string().optional(),
  type: z.string().min(1),
  category: z.string().min(1),
  daysOfWeek: z.array(z.string()).optional(),
  bannerImageUrl: z.string().optional(),
  shareText: z.string().optional(),
  isActive: z.boolean().optional(),
  betfredEnabled: z.boolean().optional(),
  lastResetDate: z.string().optional(),
  dailyStartHour: z.number().optional(),
  isRecurring: z.boolean().optional(),
  requiredHashtag: z.string().optional(),
  shareRules: z.any().optional(),
  facebookFormId: z.string().optional(),
  facebookPageId: z.string().optional(),
  facebookFormUrl: z.string().optional(),
  termsAndConditionsDialog: z.string().optional(), // Added this field
  createdBy: z.number().optional(),
});

export const insertQuestionSchema = z.object({
  competitionId: z.number().optional(),
  questionText: z.string().min(1),
  questionType: z.string().min(1),
  options: z.any().optional(),
  correctAnswer: z.string().optional(),
  isRequired: z.boolean().optional(),
  order: z.number().optional(),
  isActive: z.boolean().optional(),
  globalQuestionId: z.number().optional(),
  brandQuestionId: z.number().optional(),
  behavioralQuestionId: z.number().optional(),
  category: z.string().optional(),
  subCategory: z.string().optional(),
  keywords: z.array(z.string()).optional(),
  difficulty: z.string().optional(),
});

export const insertAnswerSchema = z.object({
  questionId: z.number().optional(),
  userId: z.number().optional(),
  competitionId: z.number().optional(),
  answerText: z.string().min(1),
});

export const insertEntrySchema = z.object({
  userId: z.number().optional(),
  competitionId: z.number().optional(),
  entryType: z.string().min(1),
  platform: z.string().optional(),
  referralId: z.number().optional(),
  metadata: z.any().optional(),
});

export const insertCompetitionCouponSchema = z.object({
  competitionId: z.number(),
  code: z.string().min(3).max(50),
  entriesPerRedemption: z.number().min(1).max(100).default(1),
  maxRedemptionsTotal: z.number().min(1).optional(),
  maxRedemptionsPerUser: z.number().min(1).optional(),
  validFrom: z.string().optional(),
  validUntil: z.string().optional(),
  status: z.enum(["active", "inactive", "expired"]).optional(),
  metadata: z.any().optional(),
});

export const insertSocialShareSchema = z.object({
  userId: z.number(),
  competitionId: z.number(),
  platform: z.string().min(1),
  shareUrl: z.string().optional(),
  shareDate: z.string(),
  shareType: z.string().optional(),
  entryId: z.number().optional(),
  status: z.string().optional(),
  hashtagUsed: z.string().optional(),
  externalPostId: z.string().optional(),
  verifiedAt: z.string().optional(),
  metadata: z.any().optional(),
  errorReason: z.string().optional(),
  multiplierApplied: z.string().optional(),
  tierAtShare: z.string().optional(),
  attemptCount: z.number().optional(),
});

export const insertReferralSchema = z.object({
  referrerId: z.number(),
  referredUserId: z.number(),
  competitionId: z.number().optional(),
  referralCode: z.string().min(1),
  referrerEntryId: z.number().optional(),
  referredEntryId: z.number().optional(),
});

export const insertBetfredSignupSchema = z.object({
  userId: z.number(),
  competitionId: z.number().optional(),
  challengeId: z.number().optional(),
  betfredEmail: z.string().email(),
});

export const insertWinnerSchema = z.object({
  competitionId: z.number(),
  userId: z.number(),
  entryId: z.number(),
  prizeClaimed: z.boolean().optional(),
  claimedDate: z.string().optional(),
  adminNotes: z.string().optional(),
  isDeleted: z.boolean().optional(),
});

export const insertSystemSettingSchema = z.object({
  key: z.string().min(1),
  value: z.string().min(1),
  description: z.string().optional(),
  updatedBy: z.number().optional(),
});

export const insertTrackingCodeSchema = z.object({
  name: z.string().min(1).max(255),
  code: z.string().min(1),
  position: z.string().min(1).max(10),
  isActive: z.boolean().optional(),
  description: z.string().optional(),
  createdBy: z.number().optional(),
});

export const insertBlogPostSchema = z.object({
  title: z.string().min(1).max(255),
  slug: z.string().min(1).max(255),
  excerpt: z.string().optional(),
  content: z.string().min(1),
  metaTitle: z.string().max(60).optional(),
  metaDescription: z.string().max(160).optional(),
  keywords: z.string().optional(),
  featuredImage: z.string().max(500).optional(),
  imageAlt: z.string().max(255).optional(),
  status: z.string().optional(),
  category: z.string().max(100).optional(),
  tags: z.array(z.string()).optional(),
  readTime: z.number().optional(),
  isPublished: z.boolean().optional(),
  publishedAt: z.string().optional(),
  authorId: z.number(),
});

export const updateBlogPostSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    slug: z.string().min(1).max(255).optional(),
    excerpt: z.string().optional(),
    content: z.string().min(1).optional(),
    metaTitle: z.string().max(60).optional(),
    metaDescription: z.string().max(160).optional(),
    keywords: z.string().optional(),
    featuredImage: z.string().max(500).optional(),
    imageAlt: z.string().max(255).optional(),
    status: z.string().optional(),
    category: z.string().max(100).optional(),
    tags: z.array(z.string()).optional(),
    readTime: z.number().optional(),
    isPublished: z.boolean().optional(),
    publishedAt: z.string().optional(),
    authorId: z.number().optional(),
    viewCount: z.number().optional(),
  })
  .partial();

export const insertRoadmapItemSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().optional(),
  status: z.string().optional(),
  priority: z.string().optional(),
  category: z.string().optional(),
  targetDate: z.string().optional(),
  completedDate: z.string().optional(),
  assignedTo: z.string().max(255).optional(),
  tags: z.array(z.string()).optional(),
  isGlobal: z.boolean().optional(),
  createdBy: z.number().optional(),
});

// Question schemas
export const insertGlobalQuestionSchema = z.object({
  questionText: z.string().min(1),
  questionType: z.string().min(1),
  options: z.any().optional(),
  correctAnswer: z.string().optional(),
  category: z.string().optional(),
  difficulty: z.string().optional(),
  isActive: z.boolean().optional(),
  createdBy: z.number().optional(),
});

export const insertBrandQuestionSchema = z.object({
  questionText: z.string().min(1),
  questionType: z.string().min(1),
  options: z.any().optional(),
  correctAnswer: z.string().optional(),
  category: z.string().optional(),
  difficulty: z.string().optional(),
  vendorId: z.number().optional(),
  isActive: z.boolean().optional(),
  createdBy: z.number().optional(),
});

export const insertBehavioralQuestionSchema = z.object({
  questionText: z.string().min(1),
  questionType: z.string().min(1),
  options: z.any().optional(),
  correctAnswer: z.string().optional(),
  category: z.string().optional(),
  difficulty: z.string().optional(),
  isActive: z.boolean().optional(),
  createdBy: z.number().optional(),
});

// Progressive Profiling schemas
export const insertProfileQuestionSchema = z.object({
  questionText: z.string().min(1),
  questionType: z.string().min(1),
  options: z.any().optional(),
  category: z.string().min(1),
  subcategory: z.string().optional(),
  tier: z.string().min(1),
  priority: z.number().optional(),
  isRequired: z.boolean().optional(),
  skipLogic: z.any().optional(),
  validationRules: z.any().optional(),
  personalityInsight: z.string().optional(),
  segmentWeight: z.string().optional(),
  businessValue: z.string().optional(),
  isActive: z.boolean().optional(),
  createdBy: z.number().optional(),
});

export const insertProfileAnswerSchema = z.object({
  userId: z.number(),
  questionId: z.number(),
  answerValue: z.string().optional(),
  selectedOptions: z.any().optional(),
  confidenceScore: z.string().optional(),
  responseTime: z.number().optional(),
  sessionId: z.string().optional(),
  isSkipped: z.boolean().optional(),
  skipReason: z.string().optional(),
  metadata: z.any().optional(),
});

export const insertProfileSessionSchema = z.object({
  userId: z.number(),
  sessionId: z.string().min(1),
  tier: z.string().min(1),
  completedAt: z.string().optional(),
  questionsShown: z.number().optional(),
  questionsAnswered: z.number().optional(),
  questionsSkipped: z.number().optional(),
  totalTimeSpent: z.number().optional(),
  completionRate: z.string().optional(),
  abandonedAt: z.string().optional(),
  abandonReason: z.string().optional(),
  deviceType: z.string().optional(),
  userAgent: z.string().optional(),
  ipAddress: z.string().optional(),
  isCompleted: z.boolean().optional(),
  metadata: z.any().optional(),
});

export const insertUserTierProgressSchema = z.object({
  userId: z.number(),
  tier: z.string().min(1),
  totalQuestions: z.number().optional(),
  answeredQuestions: z.number().optional(),
  skippedQuestions: z.number().optional(),
  completionPercentage: z.string().optional(),
  isCompleted: z.boolean().optional(),
  completedAt: z.string().optional(),
  lastQuestionAt: z.string().optional(),
  estimatedTimeRemaining: z.number().optional(),
  achievements: z.any().optional(),
  tier_score: z.string().optional(),
});

// Question Analysis and User Segmentation schemas
export const insertQuestionAnalysisSchema = z.object({
  questionId: z.number().optional(),
  globalQuestionId: z.number().optional(),
  brandQuestionId: z.number().optional(),
  behavioralQuestionId: z.number().optional(),
  analysisType: z.string().optional(),
  insights: z.any().optional(),
  confidence: z.string().optional(),
  metadata: z.any().optional(),
  createdBy: z.number().optional(),
});

export const insertUserSegmentSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  criteria: z.any().optional(),
  isActive: z.boolean().optional(),
  createdBy: z.number().optional(),
});

export const insertUserSegmentAssignmentSchema = z.object({
  userId: z.number(),
  segmentId: z.number(),
  confidence: z.string().optional(),
  metadata: z.any().optional(),
  assignedBy: z.number().optional(),
});

export const insertQuestionInsightSchema = z.object({
  questionId: z.number().optional(),
  globalQuestionId: z.number().optional(),
  brandQuestionId: z.number().optional(),
  insightType: z.string().min(1),
  content: z.string().min(1),
  confidence: z.string().optional(),
  metadata: z.any().optional(),
  createdBy: z.number().optional(),
});

export const insertUserAnalysisCacheSchema = z.object({
  userId: z.number(),
  analysisType: z.string().min(1),
  insights: z.any(),
  confidence: z.string().optional(),
  expiresAt: z.string().optional(),
  metadata: z.any().optional(),
});

// CRM Email Marketing schemas
export const insertEmailTemplateSchema = z.object({
  name: z.string().min(1).max(255),
  subject: z.string().min(1).max(255),
  bodyHtml: z.string().min(1),
  bodyText: z.string().optional(),
  category: z.string().optional(),
  variables: z.any().optional(),
  isActive: z.boolean().optional(),
  createdBy: z.number().optional(),
});

// Email Campaigns schemas
export const insertEmailCampaignSchema = z
  .object({
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    criteriaId: z.number().optional(),
    targetSegmentId: z.number().optional(),
    status: z.string().optional(),
    templateId: z.number().optional(),
    sesTemplateName: z.string().min(1).optional(),
    channel: z.enum(["email", "sms", "in_app"]).optional(),
    smsTemplateId: z.number().optional(),
    scheduledAt: z
      .preprocess((val) => {
        if (!val || val === "" || val === null) return undefined;
        if (val instanceof Date) return val;
        if (typeof val === "string") return new Date(val);
        return undefined;
      }, z.date().optional())
      .optional(),
    throttlePerMin: z.number().optional(),
    utm: z.any().optional(),
    createdBy: z.number().optional(),
  })
  .superRefine((data, ctx) => {
    const channel = data.channel || "email";
    const hasCriteria = typeof data.criteriaId === "number";
    const hasSegment = typeof data.targetSegmentId === "number";

    if (!hasCriteria && !hasSegment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either criteriaId or targetSegmentId is required",
        path: ["criteriaId"],
      });
    }

    if (channel === "sms" || channel === "in_app") {
      if (!data.smsTemplateId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "SMS template is required for SMS and in-app campaigns",
          path: ["smsTemplateId"],
        });
      }
      return;
    }

    if (!data.templateId && !data.sesTemplateName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Template is required (templateId or sesTemplateName)",
        path: ["templateId"],
      });
    }
  });

export const selectEmailCampaignSchema = createSelectSchema(emailCampaigns);

// User Criteria schemas
export const insertUserCriteriaSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  criteriaType: z.string().min(1).max(100),
  criteriaParams: z.any(),
  isActive: z.boolean().optional(),
  createdBy: z.number().optional(),
});

export const selectUserCriteriaSchema = createSelectSchema(userCriteria);
export const insertUserCriteriaBulkImportSchema = createInsertSchema(
  userCriteriaBulkImports
);
export const insertUserCriteriaBulkImportContactSchema = createInsertSchema(
  userCriteriaBulkImportContacts
);

export const insertCampaignStepSchema = z.object({
  campaignId: z.number(),
  name: z.string().min(1).max(255),
  stepType: z.string().min(1),
  templateId: z.number().optional(),
  delayHours: z.number().optional(),
  conditions: z.any().optional(),
  isActive: z.boolean().optional(),
  order: z.number().optional(),
});

export const insertUserJourneySchema = z.object({
  userId: z.number(),
  campaignId: z.number(),
  currentStepId: z.number().optional(),
  status: z.string().optional(),
  completedAt: z.string().optional(),
  metadata: z.any().optional(),
});

export const insertEmailSendSchema = z.object({
  userId: z.number(),
  campaignId: z.number().optional(),
  stepId: z.number().optional(),
  templateId: z.number(),
  toEmail: z.string().email(),
  fromEmail: z.string().email().optional(),
  subject: z.string().min(1),
  content: z.string().min(1),
  status: z.string().optional(),
  failureReason: z.string().optional(),
  sentAt: z.string().optional(),
  metadata: z.any().optional(),
});

export const insertEmailEventSchema = z.object({
  sendId: z.number(),
  type: z.string().max(50),
  eventData: z.any().optional(),
  metadata: z.any().optional(),
});

// User-Created Journey Builder schemas
export const insertUserJourneyTemplateSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  templateType: z.string().optional(),
  category: z.string().optional(),
  triggerEvent: z.string().optional(),
  triggerSettings: z.any().optional(),
  estimatedDuration: z.number().optional(),
  targetAudience: z.string().optional(),
  goals: z.array(z.string()).optional(),
  isPublic: z.boolean().optional(),
  isSystemTemplate: z.boolean().optional(),
  version: z.string().optional(),
  status: z.string().optional(),
  tags: z.array(z.string()).optional(),
  metadata: z.any().optional(),
  createdBy: z.number(),
  lastModifiedBy: z.number().optional(),
});

export const insertCustomJourneySchema = z.object({
  templateId: z.number().optional(),
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  journeyType: z.string().optional(),
  triggerEvent: z.string().optional(),
  triggerConditions: z.any().optional(),
  targetSegmentId: z.number().optional(),
  schedulingSettings: z.any().optional(),
  sesTemplateName: z.string().max(255).nullable().optional(),
  status: z.string().optional(),
  isTemplate: z.boolean().optional(),
  version: z.string().optional(),
  parentJourneyId: z.number().optional(),
  totalSteps: z.number().optional(),
  estimatedDuration: z.number().optional(),
  metrics: z.any().optional(),
  settings: z.any().optional(),
  tags: z.array(z.string()).optional(),
  metadata: z.any().optional(),
  createdBy: z.number(),
  lastModifiedBy: z.number().optional(),
  publishedAt: z.string().optional(),
  archivedAt: z.string().optional(),
});

export const insertCustomEmailTemplateSchema = z.object({
  journeyId: z.number().optional(),
  name: z.string().min(1).max(255),
  subject: z.string().min(1).max(255),
  bodyHtml: z.string().min(1),
  bodyText: z.string().optional(),
  previewText: z.string().max(250).optional(),
  templateType: z.string().optional(),
  category: z.string().optional(),
  variables: z.any().optional(),
  designSettings: z.any().optional(),
  isShared: z.boolean().optional(),
  isApproved: z.boolean().optional(),
  parentTemplateId: z.number().optional(),
  version: z.string().optional(),
  tags: z.array(z.string()).optional(),
  status: z.string().optional(),
  metadata: z.any().optional(),
  createdBy: z.number(),
  lastModifiedBy: z.number().optional(),
  approvedBy: z.number().optional(),
  approvedAt: z.string().optional(),
});

export const insertJourneyStepSchema = z.object({
  journeyId: z.number(),
  parentStepId: z.number().optional(),
  stepType: z.string().min(1),
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  stepIndex: z.number(),
  emailTemplateId: z.number().optional(),
  delaySettings: z.any().optional(),
  actionSettings: z.any().optional(),
  webhookSettings: z.any().optional(),
  splitTestSettings: z.any().optional(),
  isActive: z.boolean().optional(),
  isConditional: z.boolean().optional(),
  position: z.any().optional(),
  config: z.any().optional(),
  metadata: z.any().optional(),
  createdBy: z.number(),
});

export const insertJourneyConditionSchema = z.object({
  stepId: z.number(),
  conditionType: z.string().min(1),
  field: z.string().min(1).max(100),
  operator: z.string().min(1).max(20),
  value: z.string().optional(),
  logicalOperator: z.string().optional(),
  conditionOrder: z.number().optional(),
  trueStepId: z.number().optional(),
  falseStepId: z.number().optional(),
  isActive: z.boolean().optional(),
  metadata: z.any().optional(),
});

export const insertJourneyEmailLogSchema = z.object({
  journeyId: z.number(),
  stepId: z.number(),
  userId: z.number(),
  triggerEvent: z.string().min(1).max(100),
  emailTemplateId: z.number().optional(),
  emailSendId: z.number().optional(),
  status: z.enum(["sent", "failed", "skipped", "scheduled"]).optional(),
  sentAt: z.string().optional(),
  failureReason: z.string().optional(),
  metadata: z.any().optional(),
});

// Engagement trigger tracking schemas
export const insertEngagementTriggerTrackingSchema = z.object({
  userId: z.number(),
  triggerType: z.enum(["unanswered_questions", "missing_competitions"]),
  lastTriggeredAt: z.string().optional(),
  metadata: z.any().optional(),
});

export const selectEngagementTriggerTrackingSchema = createSelectSchema(
  engagementTriggerTracking
);

// System configuration schemas
export const insertSettingSchema = z.object({
  key: z.string().min(1).max(255),
  value: z.string(),
  category: z.string().optional(),
  description: z.string().optional(),
  isPublic: z.boolean().optional(),
});

export const insertRoleSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  permissions: z.any().optional(),
});

// Bug tracking schemas
export const insertBugSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().min(1),
  severity: z.string().optional(),
  priority: z.string().optional(),
  status: z.string().optional(),
  assignedTo: z.number().optional(),
  reportedBy: z.number(),
  reproductionSteps: z.string().optional(),
  expectedBehavior: z.string().optional(),
  actualBehavior: z.string().optional(),
  environment: z.string().optional(),
  browserInfo: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

export const insertTestCaseSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().min(1),
  steps: z.string().min(1),
  expectedResult: z.string().min(1),
  priority: z.string().optional(),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  isAutomated: z.boolean().optional(),
  createdBy: z.number(),
});

export const insertTestRunSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  environment: z.string().optional(),
  browserInfo: z.string().optional(),
  status: z.string().optional(),
  completedAt: z.string().optional(),
  createdBy: z.number(),
});

export const insertTestExecutionSchema = z.object({
  testRunId: z.number(),
  testCaseId: z.number(),
  status: z.string(),
  actualResult: z.string().optional(),
  notes: z.string().optional(),
  screenshots: z.array(z.string()).optional(),
  executedBy: z.number(),
});

// Social Media schemas
export const insertSocialChannelSchema = z.object({
  platform: z.string().min(1).max(50),
  accountName: z.string().min(1).max(255),
  isActive: z.boolean().optional(),
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
  accountId: z.string().optional(),
  metadata: z.any().optional(),
});

export const insertSocialPostSchema = z.object({
  channelId: z.number(),
  content: z.string().min(1),
  mediaUrls: z.array(z.string()).optional(),
  scheduledAt: z.string().optional(),
  publishedAt: z.string().optional(),
  status: z.string().optional(),
  platformPostId: z.string().optional(),
  hashtags: z.array(z.string()).optional(),
  mentions: z.array(z.string()).optional(),
  metadata: z.any().optional(),
});

export const insertSocialAnalyticsSchema = z.object({
  postId: z.number(),
  platform: z.string().min(1).max(50),
  impressions: z.number().optional(),
  reach: z.number().optional(),
  engagement: z.number().optional(),
  clicks: z.number().optional(),
  shares: z.number().optional(),
  saves: z.number().optional(),
  comments: z.number().optional(),
  likes: z.number().optional(),
  metadata: z.any().optional(),
});

export const insertSocialTemplateSchema = z.object({
  name: z.string().min(1).max(255),
  platform: z.string().min(1).max(50),
  content: z.string().min(1),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  isActive: z.boolean().optional(),
  variables: z.any().optional(),
  createdBy: z.number(),
});

export const insertSocialSchedulingRuleSchema = z.object({
  name: z.string().min(1).max(255),
  platform: z.string().min(1).max(50),
  timeSlots: z.any(),
  frequency: z.string(),
  isActive: z.boolean().optional(),
  priority: z.number().optional(),
  conditions: z.any().optional(),
});

export const insertSocialContentCalendarSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().optional(),
  scheduledDate: z.string(),
  platform: z.string().min(1).max(50),
  content: z.string().min(1),
  mediaUrls: z.array(z.string()).optional(),
  status: z.string().optional(),
  assignedTo: z.number().optional(),
  campaignId: z.number().optional(),
  hashtags: z.array(z.string()).optional(),
  mentions: z.array(z.string()).optional(),
});

export const insertSocialAudienceInsightsSchema = z.object({
  platform: z.string().min(1).max(50),
  audienceSize: z.number().optional(),
  demographics: z.any().optional(),
  interests: z.any().optional(),
  engagementRate: z.number().optional(),
  topLocations: z.any().optional(),
  ageGroups: z.any().optional(),
  genderSplit: z.any().optional(),
  peakActivity: z.any().optional(),
  metadata: z.any().optional(),
});

// Spinner schemas
export const insertSpinnerEntrySchema = z.object({
  userId: z.number(),
  competitionId: z.number(),
  totalEntries: z.number().default(1),
  spinCredits: z.number().default(1),
  metadata: z.any().optional(),
});

export const insertSpinnerResultSchema = z.object({
  userId: z.number(),
  competitionId: z.number(),
  entryId: z.number().optional(),
  outcome: z.string(),
  prize: z.string().optional(),
  prizeValue: z.number().optional(),
  spinData: z.any().optional(),
  metadata: z.any().optional(),
});

// Communications schemas
export const insertCommunicationSchema = z.object({
  userId: z.number(),
  type: z.string().min(1).max(50),
  title: z.string().min(1).max(255),
  content: z.string().min(1),
  isRead: z.boolean().optional(),
  priority: z.string().optional(),
  metadata: z.any().optional(),
});

// Vendors schemas
export const insertVendorSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  logoUrl: z.string().optional(),
  websiteUrl: z.string().optional(),
  contactEmail: z.string().email().optional(),
  contactPhone: z.string().optional(),
  category: z.string().optional(),
  isActive: z.boolean().optional(),
  metadata: z.any().optional(),
});

// Terms and Conditions schema
export const insertTermsAndConditionsSchema = createInsertSchema(
  termsAndConditions
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Terms and Conditions types
export type TermsAndConditions = typeof termsAndConditions.$inferSelect;
export type InsertTermsAndConditions = z.infer<
  typeof insertTermsAndConditionsSchema
>;

// Challenge schemas - manually defined to match table structure
export const insertChallengeSchema = z.object({
  type: z.string(),
  name: z.string(),
  description: z.string().optional(),
  startDatetime: z.date(),
  endDatetime: z.date().optional(),
  status: z.string().optional().default("draft"),
  rulesJson: z.any(),
  prize1: z.string().optional(),
  prize2: z.string().optional(),
  prize3: z.string().optional(),
  targetValue: z.number().optional(),
  isActive: z.boolean().optional().default(true),
  isVisible: z.boolean().optional().default(true),
  customRules: z.string().optional(),
  leftImageUrl: z.string().optional(),
  rightImageUrl: z.string().optional(),
  reviewMilestones: z.string().optional(),
  createdBy: z.number().optional(),
});

export const insertUserChallengeProgressSchema = z.object({
  userId: z.number(),
  challengeId: z.number(),
  progressCount: z.number().optional().default(0),
  completed: z.boolean().optional().default(false),
});

export const insertChallengeActionSchema = z.object({
  userId: z.number().optional(),
  challengeId: z.number().optional(),
  actionType: z.string(),
  actionData: z.any().optional(),
  pointsEarned: z.number().optional().default(0),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
});

export const insertChallengeReferralSchema = z.object({
  challengeId: z.number(),
  referrerId: z.number(),
  referredEmail: z.string(),
  referredUserId: z.number().optional(),
  referralCode: z.string(),
  status: z.string().optional().default("pending"),
  socialSource: z.string().optional(),
  verifiedAt: z.date().optional(),
  ipAddress: z.string().optional(),
});

export const insertPendingReferralSchema = z.object({
  challengeId: z.number(),
  referralCode: z.string().min(1).max(20),
  socialSource: z.string().optional(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  sessionId: z.string().optional(),
  expiresAt: z.string(), // ISO timestamp string
});

export const insertChallengeWinnerSchema = z.object({
  challengeId: z.number(),
  userId: z.number(),
  position: z.number().min(1).max(3), // 1, 2, or 3
  progressCount: z.number().min(0),
  referralCount: z.number().min(0).optional().default(0),
  winningType: z.enum(["target_reached", "referral_ranking"]),
  prize: z.string().optional(),
});

// AI Chat schemas
export const insertAiConversationSchema = z.object({
  userId: z.number(),
  title: z.string().optional(),
  model: z.string().optional(),
  systemPrompt: z.string().optional(),
  isActive: z.boolean().optional(),
  metadata: z.any().optional(),
});

export const insertAiMessageSchema = z.object({
  conversationId: z.number(),
  role: z.string().min(1).max(20),
  content: z.string().min(1),
  model: z.string().optional(),
  tokens: z.number().optional(),
  metadata: z.any().optional(),
});

export const insertAiArtifactSchema = z.object({
  conversationId: z.number(),
  messageId: z.number(),
  title: z.string().min(1).max(255),
  type: z.string().min(1).max(50),
  content: z.string().min(1),
  language: z.string().optional(),
  metadata: z.any().optional(),
});

// Share verification schemas
export const insertShareIntentSchema = z.object({
  userId: z.number(),
  competitionId: z.number(),
  platform: z.string().min(1).max(50),
  shareUrl: z.string().url(),
  shareText: z.string().optional(),
  status: z.string().optional(),
  metadata: z.any().optional(),
});

export const insertShareVerificationSchema = z.object({
  shareIntentId: z.number(),
  verificationMethod: z.string().min(1).max(50),
  verificationData: z.any(),
  isVerified: z.boolean().optional(),
  verifiedAt: z.string().optional(),
  bonusMultiplier: z.number().optional(),
  tierBonusApplied: z.boolean().optional(),
  metadata: z.any().optional(),
});

// User Journey Triggers schemas
export const insertUserJourneyTriggerSchema = z.object({
  key: z.string().min(1).max(255),
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  source: z.enum(["crm", "webhook", "time"]),
  sourceEventTypes: z.array(z.string()).min(1),
  payloadSchema: z.any().optional(),
  defaultFilter: z.any().optional(),
  status: z.enum(["active", "disabled"]).optional(),
});

export const selectUserJourneyTriggerSchema =
  createSelectSchema(userJourneyTriggers);

// ===== FORM VALIDATION SCHEMAS =====
export const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

export const registerSchema = insertUserSchema
  .extend({
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(6, "Confirm password is required"),
    phone: z
      .string()
      .min(10, "Contact number must be at least 10 digits")
      .regex(/^[\d\s+()-]+$/, "Invalid phone number format"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export const competitionFormSchema = z
  .object({
    title: z
      .string()
      .min(1, "Title is required")
      .max(255, "Title must be less than 255 characters"),
    category: z.string().min(1, "Category is required"),
    type: z.string().min(1, "Type is required"),
    status: z.enum(["draft", "active", "ended"]).default("draft"),
    startDate: z.string().min(1, "Start date is required"),
    startTime: z.string().min(1, "Start time is required"),
    endDate: z.string().min(1, "End date is required"),
    endTime: z.string().min(1, "End time is required"),
    prize1: z.string().optional(),
    prize2: z.string().optional(),
    prize3: z.string().optional(),
    bannerImageUrl: z
      .string()
      .url("Please enter a valid URL")
      .optional()
      .or(z.literal("")),
    shareText: z.string().optional(),
    daysOfWeek: z.array(z.string()).optional(),
    createFacebookForm: z.boolean().optional(),
    facebookPageId: z.string().optional(),
    termsAndConditionsDialog: z.string().optional(),
    betfredEnabled: z.boolean().optional().default(true), // Enable Betfred by default
  })
  .refine(
    (data) => {
      // Validate that start time is before end time
      const startDateTime = new Date(`${data.startDate}T${data.startTime}:00`);
      const endDateTime = new Date(`${data.endDate}T${data.endTime}:00`);
      return startDateTime < endDateTime;
    },
    {
      message: "Start date and time must be before end date and time",
      path: ["endDate"],
    }
  );

// ===== TYPE DEFINITIONS =====
// Core types
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type UpdateUserProfile = z.infer<typeof updateUserProfileSchema>;
export type Admin = typeof admins.$inferSelect;

export type Competition = typeof competitions.$inferSelect;
export type InsertCompetition = z.infer<typeof insertCompetitionSchema>;
export type CompetitionForm = z.infer<typeof competitionFormSchema>;

export type Question = typeof questions.$inferSelect;
export type InsertQuestion = z.infer<typeof insertQuestionSchema>;

export type Answer = typeof answers.$inferSelect;
export type InsertAnswer = z.infer<typeof insertAnswerSchema>;

export type Entry = typeof entries.$inferSelect;
export type InsertEntry = z.infer<typeof insertEntrySchema>;

// Share verification types
export type ShareIntent = typeof shareIntents.$inferSelect;
export type InsertShareIntent = z.infer<typeof insertShareIntentSchema>;

export type ShareVerification = typeof shareVerifications.$inferSelect;
export type InsertShareVerification = z.infer<
  typeof insertShareVerificationSchema
>;

export type SocialShare = typeof socialShares.$inferSelect;
export type InsertSocialShare = z.infer<typeof insertSocialShareSchema>;

export type Referral = typeof referrals.$inferSelect;
export type InsertReferral = z.infer<typeof insertReferralSchema>;

export type BetfredSignup = typeof betfredSignups.$inferSelect;
export type InsertBetfredSignup = z.infer<typeof insertBetfredSignupSchema>;

export type CompetitionCoupon = typeof competitionCoupons.$inferSelect;
export type InsertCompetitionCoupon = z.infer<
  typeof insertCompetitionCouponSchema
>;

export type Winner = typeof winners.$inferSelect;
export type InsertWinner = z.infer<typeof insertWinnerSchema>;

export type SystemSetting = typeof systemSettings.$inferSelect;
export type InsertSystemSetting = z.infer<typeof insertSystemSettingSchema>;

export type TrackingCode = typeof trackingCodes.$inferSelect;
export type InsertTrackingCode = z.infer<typeof insertTrackingCodeSchema>;

export type BlogPost = typeof blogPosts.$inferSelect;
export type InsertBlogPost = z.infer<typeof insertBlogPostSchema>;
export type UpdateBlogPost = z.infer<typeof updateBlogPostSchema>;

export type RoadmapItem = typeof roadmapItems.$inferSelect;
export type InsertRoadmapItem = z.infer<typeof insertRoadmapItemSchema>;

// Question types
export type GlobalQuestion = typeof globalQuestions.$inferSelect;
export type InsertGlobalQuestion = z.infer<typeof insertGlobalQuestionSchema>;

export type BrandQuestion = typeof brandQuestions.$inferSelect;
export type InsertBrandQuestion = z.infer<typeof insertBrandQuestionSchema>;

export type BehavioralQuestion = typeof behavioralQuestions.$inferSelect;
export type InsertBehavioralQuestion = z.infer<
  typeof insertBehavioralQuestionSchema
>;

// Progressive Profiling types
export type ProfileQuestion = typeof profileQuestions.$inferSelect;
export type InsertProfileQuestion = z.infer<typeof insertProfileQuestionSchema>;

export type ProfileAnswer = typeof profileAnswers.$inferSelect;
export type InsertProfileAnswer = z.infer<typeof insertProfileAnswerSchema>;

export type ProfileSession = typeof profileSessions.$inferSelect;
export type InsertProfileSession = z.infer<typeof insertProfileSessionSchema>;

export type UserTierProgress = typeof userTierProgress.$inferSelect;
export type InsertUserTierProgress = z.infer<
  typeof insertUserTierProgressSchema
>;

// System configuration types
export type Setting = typeof settings.$inferSelect;
export type InsertSetting = z.infer<typeof insertSettingSchema>;

export type Role = typeof roles.$inferSelect;
export type InsertRole = z.infer<typeof insertRoleSchema>;

export type Organization = typeof organizations.$inferSelect;
export type AuditLog = typeof auditLogs.$inferSelect;

// Bug tracking types
export type Bug = typeof bugs.$inferSelect;
export type InsertBug = z.infer<typeof insertBugSchema>;

export type TestCase = typeof testCases.$inferSelect;
export type InsertTestCase = z.infer<typeof insertTestCaseSchema>;

export type TestRun = typeof testRuns.$inferSelect;
export type InsertTestRun = z.infer<typeof insertTestRunSchema>;

export type TestExecution = typeof testExecutions.$inferSelect;
export type InsertTestExecution = z.infer<typeof insertTestExecutionSchema>;

// AI Chat types
export type AiConversation = typeof aiConversations.$inferSelect;
export type InsertAiConversation = z.infer<typeof insertAiConversationSchema>;

export type AiMessage = typeof aiMessages.$inferSelect;
export type InsertAiMessage = z.infer<typeof insertAiMessageSchema>;

export type AiArtifact = typeof aiArtifacts.$inferSelect;
export type InsertAiArtifact = z.infer<typeof insertAiArtifactSchema>;

// Social Media types
export type SocialChannel = typeof socialChannels.$inferSelect;
export type InsertSocialChannel = z.infer<typeof insertSocialChannelSchema>;

export type SocialPost = typeof socialPosts.$inferSelect;
export type InsertSocialPost = z.infer<typeof insertSocialPostSchema>;

export type SocialAnalytics = typeof socialAnalytics.$inferSelect;
export type InsertSocialAnalytics = z.infer<typeof insertSocialAnalyticsSchema>;

export type SocialTemplate = typeof socialTemplates.$inferSelect;
export type InsertSocialTemplate = z.infer<typeof insertSocialTemplateSchema>;

export type SocialSchedulingRule = typeof socialSchedulingRules.$inferSelect;
export type InsertSocialSchedulingRule = z.infer<
  typeof insertSocialSchedulingRuleSchema
>;

export type SocialContentCalendar = typeof socialContentCalendar.$inferSelect;
export type InsertSocialContentCalendar = z.infer<
  typeof insertSocialContentCalendarSchema
>;

export type SocialAudienceInsights = typeof socialAudienceInsights.$inferSelect;
export type InsertSocialAudienceInsights = z.infer<
  typeof insertSocialAudienceInsightsSchema
>;

// Daily Reset Log types
export type DailyResetLog = typeof dailyResetLog.$inferSelect;
export type InsertDailyResetLog = typeof dailyResetLog.$inferInsert;

// Spinner types
export type SpinnerEntry = typeof spinnerEntries.$inferSelect;
export type InsertSpinnerEntry = z.infer<typeof insertSpinnerEntrySchema>;

export type SpinnerResult = typeof spinnerResults.$inferSelect;
export type InsertSpinnerResult = z.infer<typeof insertSpinnerResultSchema>;

// Communications types
export type Communication = typeof communications.$inferSelect;
export type InsertCommunication = z.infer<typeof insertCommunicationSchema>;

// Vendors types
export type Vendor = typeof vendors.$inferSelect;
export type InsertVendor = z.infer<typeof insertVendorSchema>;

// Challenge types
export type Challenge = typeof challenges.$inferSelect;
export type InsertChallenge = z.infer<typeof insertChallengeSchema>;

export type UserChallengeProgress = typeof userChallengeProgress.$inferSelect;
export type InsertUserChallengeProgress = z.infer<
  typeof insertUserChallengeProgressSchema
>;

export type ChallengeAction = typeof challengeActions.$inferSelect;
export type InsertChallengeAction = z.infer<typeof insertChallengeActionSchema>;

export type ChallengeReferral = typeof challengeReferrals.$inferSelect;
export type InsertChallengeReferral = z.infer<
  typeof insertChallengeReferralSchema
>;

export type PendingReferral = typeof pendingReferrals.$inferSelect;
export type InsertPendingReferral = z.infer<typeof insertPendingReferralSchema>;

export type ChallengeWinner = typeof challengeWinners.$inferSelect;
export type InsertChallengeWinner = z.infer<typeof insertChallengeWinnerSchema>;

export type LeaderboardCache = typeof leaderboardCache.$inferSelect;

// Question Analysis and User Segmentation types
export type QuestionAnalysis = typeof questionAnalysis.$inferSelect;
export type InsertQuestionAnalysis = z.infer<
  typeof insertQuestionAnalysisSchema
>;

export type UserSegment = typeof userSegments.$inferSelect;
export type InsertUserSegment = z.infer<typeof insertUserSegmentSchema>;

export type UserSegmentAssignment = typeof userSegmentAssignments.$inferSelect;
export type InsertUserSegmentAssignment = z.infer<
  typeof insertUserSegmentAssignmentSchema
>;

export type QuestionSegmentMapping = typeof questionSegmentMapping.$inferSelect;
export type InsertQuestionSegmentMapping =
  typeof questionSegmentMapping.$inferInsert;

export type QuestionInsight = typeof questionInsights.$inferSelect;
export type InsertQuestionInsight = z.infer<typeof insertQuestionInsightSchema>;

export type UserAnalysisCache = typeof userAnalysisCache.$inferSelect;
export type InsertUserAnalysisCache = z.infer<
  typeof insertUserAnalysisCacheSchema
>;

// CRM Email Marketing types
export type EmailTemplate = typeof emailTemplates.$inferSelect;
export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;

export type EmailCampaign = typeof emailCampaigns.$inferSelect;
export type InsertEmailCampaign = z.infer<typeof insertEmailCampaignSchema>;

export type UserCriteria = typeof userCriteria.$inferSelect;
export type InsertUserCriteria = z.infer<typeof insertUserCriteriaSchema>;
export type UserCriteriaBulkImport =
  typeof userCriteriaBulkImports.$inferSelect;
export type InsertUserCriteriaBulkImport =
  typeof userCriteriaBulkImports.$inferInsert;
export type UserCriteriaBulkImportContact =
  typeof userCriteriaBulkImportContacts.$inferSelect;
export type InsertUserCriteriaBulkImportContact =
  typeof userCriteriaBulkImportContacts.$inferInsert;
export type UserCriteriaBulkImportConversion =
  typeof userCriteriaBulkImportConversions.$inferSelect;
export type InsertUserCriteriaBulkImportConversion =
  typeof userCriteriaBulkImportConversions.$inferInsert;

export type CampaignStep = typeof campaignSteps.$inferSelect;
export type InsertCampaignStep = z.infer<typeof insertCampaignStepSchema>;

export type UserJourney = typeof userJourneys.$inferSelect;
export type InsertUserJourney = z.infer<typeof insertUserJourneySchema>;

export type EmailSend = typeof emailSends.$inferSelect;
export type InsertEmailSend = z.infer<typeof insertEmailSendSchema>;

// User Journey Triggers types
export type UserJourneyTrigger = typeof userJourneyTriggers.$inferSelect;
export type InsertUserJourneyTrigger = z.infer<
  typeof insertUserJourneyTriggerSchema
>;

export type EmailEvent = typeof emailEvents.$inferSelect;
export type InsertEmailEvent = z.infer<typeof insertEmailEventSchema>;

// User-Created Journey Builder types
export type UserJourneyTemplate = typeof userJourneyTemplates.$inferSelect;
export type InsertUserJourneyTemplate = z.infer<
  typeof insertUserJourneyTemplateSchema
>;

export type CustomJourney = typeof customJourneys.$inferSelect;
export type InsertCustomJourney = z.infer<typeof insertCustomJourneySchema>;

export type CustomEmailTemplate = typeof customEmailTemplates.$inferSelect;
export type InsertCustomEmailTemplate = z.infer<
  typeof insertCustomEmailTemplateSchema
>;

export type JourneyStep = typeof journeySteps.$inferSelect;
export type InsertJourneyStep = z.infer<typeof insertJourneyStepSchema>;

export type JourneyCondition = typeof journeyConditions.$inferSelect;
export type InsertJourneyCondition = z.infer<
  typeof insertJourneyConditionSchema
>;

export type JourneyEmailLog = typeof journeyEmailLogs.$inferSelect;
export type InsertJourneyEmailLog = z.infer<typeof insertJourneyEmailLogSchema>;

export type EngagementTriggerTracking =
  typeof engagementTriggerTracking.$inferSelect;
export type InsertEngagementTriggerTracking = z.infer<
  typeof insertEngagementTriggerTrackingSchema
>;

export type EmailContactOptOut = typeof emailContactOptOuts.$inferSelect;

export type EmailSuppression = typeof emailSuppressionList.$inferSelect;
export type InsertEmailSuppression = typeof emailSuppressionList.$inferInsert;

export type CampaignNotifiedUser = typeof campaignNotifiedUsers.$inferSelect;

export type UserEmailWeeklyQuota = typeof userEmailWeeklyQuota.$inferSelect;

export type InAppMessage = typeof inAppMessages.$inferSelect;

// ============================================================================
// ANONYMIZED ANALYTICS VIEW TYPES - FOR WAIN READ-ONLY ACCESS
// ============================================================================
// Type definitions for anonymized analytics views without PII exposure

export type VUsersPublic = typeof vUsersPublic.$inferSelect;
export type VCompetitionsCore = typeof vCompetitionsCore.$inferSelect;
export type VEntriesAnonymized = typeof vEntriesAnonymized.$inferSelect;
export type VReferralsSummary = typeof vReferralsSummary.$inferSelect;
export type VSocialSharesByPlatform =
  typeof vSocialSharesByPlatform.$inferSelect;
export type VWinnersSummary = typeof vWinnersSummary.$inferSelect;
export type VDailyMetrics = typeof vDailyMetrics.$inferSelect;

// User Query Logs table - for storing user support queries
export const userQueryLogs = pgTable(
  "user_query_logs",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    userEmail: varchar("user_email", { length: 255 }).notNull(), // Store user email for admin reference
    queryType: varchar("query_type", { length: 50 }).notNull(), // Predefined query types with length limit
    customMessage: varchar("custom_message", { length: 500 }), // Optional custom message with 500 char DB limit
    status: varchar("status", { length: 20 }).default("open"), // open, resolved, closed with length limit
    createdAt: timestamp("created_at").defaultNow(),
    resolvedAt: timestamp("resolved_at"),
    resolvedBy: integer("resolved_by").references(() => users.id), // Admin who resolved the query
    notes: text("notes"), // Admin notes
  },
  (table) => [
    index("idx_user_query_logs_user_id").on(table.userId),
    index("idx_user_query_logs_status").on(table.status),
    index("idx_user_query_logs_query_type").on(table.queryType),
    index("idx_user_query_logs_created_at").on(table.createdAt),
    // Add database-level constraints for data validation
    check(
      "chk_query_type_valid",
      sql`query_type IN ('cant_enter_challenges', 'cant_share_facebook', 'cant_enter_competitions', 'login_issues', 'technical_problems', 'prizes_questions', 'feedback', 'other')`
    ),
    check("chk_status_valid", sql`status IN ('open', 'resolved', 'closed')`),
    check("chk_custom_message_length", sql`LENGTH(custom_message) <= 500`),
  ]
);

// Schema definitions for user query logs

// Client-side schema (excludes server-controlled fields)
export const insertUserQueryLogSchema = z.object({
  queryType: z.string().min(1).max(50),
  customMessage: z.string().max(500).optional(),
  notes: z.string().optional(),
});

// Server-side schema (includes all fields that server needs to set)
export const serverInsertUserQueryLogSchema = z.object({
  userId: z.number(),
  userEmail: z.string().email().max(255),
  queryType: z.string().min(1).max(50),
  customMessage: z.string().max(500).optional(),
  notes: z.string().optional(),
});

export const selectUserQueryLogSchema = createSelectSchema(userQueryLogs);

export type UserQueryLog = typeof userQueryLogs.$inferSelect;
export type InsertUserQueryLog = z.infer<typeof insertUserQueryLogSchema>;
export type ServerInsertUserQueryLog = z.infer<
  typeof serverInsertUserQueryLogSchema
>;

// ============================================================================
// SMS FACTORY CONFIGURATION
// ============================================================================

// SMS Configuration table for global SMS settings
export const smsConfiguration = pgTable(
  "sms_configuration",
  {
    id: serial("id").primaryKey(),
    settingKey: varchar("setting_key", { length: 100 }).unique().notNull(),
    settingValue: text("setting_value"),
    description: text("description"),
    isActive: boolean("is_active").default(true),
    updatedAt: timestamp("updated_at").defaultNow(),
    updatedBy: integer("updated_by").references(() => users.id),
  },
  (table) => [
    index("idx_sms_config_key").on(table.settingKey),
    index("idx_sms_config_active").on(table.isActive),
  ]
);

// SMS Templates table for message templates
export const smsTemplates = pgTable(
  "sms_templates",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 100 }).notNull(),
    templateKey: varchar("template_key", { length: 100 }).unique().notNull(),
    messageContent: text("message_content").notNull(),
    description: text("description"),
    variables: jsonb("variables"), // Array of available variables like ["name", "code", "prize"]
    category: varchar("category", { length: 50 }).notNull(), // otp, competition, winner, custom
    isActive: boolean("is_active").default(true),
    usageCount: integer("usage_count").default(0),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    createdBy: integer("created_by").references(() => users.id),
  },
  (table) => [
    index("idx_sms_templates_key").on(table.templateKey),
    index("idx_sms_templates_category").on(table.category),
    index("idx_sms_templates_active").on(table.isActive),
  ]
);

// SMS Logs table for tracking sent messages
export const smsLogs = pgTable(
  "sms_logs",
  {
    id: serial("id").primaryKey(),
    recipient: varchar("recipient", { length: 20 }).notNull(),
    message: text("message").notNull(),
    templateId: integer("template_id").references(() => smsTemplates.id),
    messageId: varchar("message_id"), // From SMS Factory API
    status: varchar("status", { length: 20 }).default("sent"), // sent, failed, delivered
    errorMessage: text("error_message"),
    reference: varchar("reference", { length: 100 }),
    cost: numeric("cost", { precision: 10, scale: 2 }), // Cost of this SMS in credits
    remainingBalance: numeric("remaining_balance", { precision: 10, scale: 2 }), // Balance after sending
    sentAt: timestamp("sent_at").defaultNow(),
    deliveredAt: timestamp("delivered_at"),
    sentBy: integer("sent_by").references(() => users.id),
  },
  (table) => [
    index("idx_sms_logs_recipient").on(table.recipient),
    index("idx_sms_logs_status").on(table.status),
    index("idx_sms_logs_template").on(table.templateId),
    index("idx_sms_logs_sent_at").on(table.sentAt),
  ]
);

// SMS delivery reports from SMS Factory webhooks
export const smsDeliveryReports = pgTable(
  "sms_delivery_reports",
  {
    id: uuid("id").defaultRandom().primaryKey(),
    messageId: varchar("message_id", { length: 255 }).notNull(),
    campaignId: varchar("campaign_id", { length: 255 }),
    destination: varchar("destination", { length: 50 }),
    status: integer("status"),
    countryCode: varchar("country_code", { length: 2 }),
    deliveryDate: timestamp("delivery_date"),
    rawPayload: jsonb("raw_payload"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_sms_delivery_reports_message_id").on(table.messageId),
    index("idx_sms_delivery_reports_campaign_id").on(table.campaignId),
    index("idx_sms_delivery_reports_status").on(table.status),
  ]
);

// Facebook Lead Forms table for tracking reusable forms (competition-linked or standalone)
export const facebookLeadForms = pgTable(
  "facebook_lead_forms",
  {
    id: serial("id").primaryKey(),
    formId: varchar("form_id", { length: 100 }).notNull(),
    pageId: varchar("page_id", { length: 100 }),
    name: varchar("name", { length: 255 }),
    purpose: varchar("purpose", { length: 50 }).notNull(), // competition, signup, etc.
    description: text("description"),
    competitionId: integer("competition_id").references(() => competitions.id),
    questions: jsonb("questions"),
    metadata: jsonb("metadata"),
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_facebook_lead_forms_form_id").on(table.formId),
    index("idx_facebook_lead_forms_purpose").on(table.purpose),
    index("idx_facebook_lead_forms_competition_id").on(table.competitionId),
  ]
);

// Facebook Lead submissions table for logging webhook payloads and processing results
export const facebookLeadSubmissions = pgTable(
  "facebook_lead_submissions",
  {
    id: serial("id").primaryKey(),
    leadgenId: varchar("leadgen_id", { length: 100 }).notNull(),
    formId: varchar("form_id", { length: 100 }),
    formRecordId: integer("form_record_id").references(
      () => facebookLeadForms.id
    ),
    formPurpose: varchar("form_purpose", { length: 50 }),
    competitionId: integer("competition_id").references(() => competitions.id),
    pageId: varchar("page_id", { length: 100 }),
    adId: varchar("ad_id", { length: 100 }),
    campaignId: varchar("campaign_id", { length: 100 }),
    email: varchar("email", { length: 255 }),
    userId: integer("user_id").references(() => users.id),
    payload: jsonb("payload"),
    answers: jsonb("answers"),
    processingStatus: varchar("processing_status", { length: 20 }).default(
      "received"
    ),
    processingError: text("processing_error"),
    createdAt: timestamp("created_at").defaultNow(),
    processedAt: timestamp("processed_at"),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_facebook_lead_submissions_leadgen").on(table.leadgenId),
    index("idx_facebook_lead_submissions_form_id").on(table.formId),
    index("idx_facebook_lead_submissions_form_record_id").on(
      table.formRecordId
    ),
    index("idx_facebook_lead_submissions_purpose").on(table.formPurpose),
    index("idx_facebook_lead_submissions_competition_id").on(
      table.competitionId
    ),
    index("idx_facebook_lead_submissions_created_at").on(table.createdAt),
    check(
      "chk_facebook_lead_processing_status",
      sql`${table.processingStatus} IN ('received', 'processed', 'failed', 'skipped')`
    ),
  ]
);

export const insertFacebookLeadSubmissionSchema = createInsertSchema(
  facebookLeadSubmissions
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const selectFacebookLeadSubmissionSchema = createSelectSchema(
  facebookLeadSubmissions
);

export type FacebookLeadSubmission =
  typeof facebookLeadSubmissions.$inferSelect;
export type InsertFacebookLeadSubmission =
  typeof facebookLeadSubmissions.$inferInsert;

export const insertFacebookLeadFormSchema = createInsertSchema(
  facebookLeadForms
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const selectFacebookLeadFormSchema =
  createSelectSchema(facebookLeadForms);

export type FacebookLeadForm = typeof facebookLeadForms.$inferSelect;
export type InsertFacebookLeadForm = typeof facebookLeadForms.$inferInsert;

// Friends table for storing user's friends list
export const friends = pgTable(
  "friends",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }), // User who added the friend
    registeredUserId: integer("registered_user_id").references(() => users.id, {
      onDelete: "cascade",
    }), // Linked to actual user account if they exist in the system
    friendEmail: varchar("friend_email"),
    friendPhone: varchar("friend_phone", { length: 20 }),
    friendName: varchar("friend_name"),
    friendUsername: varchar("friend_username"),
    addedAt: timestamp("added_at").defaultNow(),
  },
  (table) => [
    index("idx_friends_user_id").on(table.userId),
    index("idx_friends_registered_user_id").on(table.registeredUserId),
    index("idx_friends_email").on(table.friendEmail),
    index("idx_friends_phone").on(table.friendPhone),
  ]
);

// Challenge Invites Tracker - tracks how many invites each user has sent per challenge
export const challengeInvites = pgTable(
  "challenge_invites",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    challengeId: integer("challenge_id")
      .notNull()
      .references(() => challenges.id, { onDelete: "cascade" }),
    inviteCount: integer("invite_count").notNull().default(0), // Number of invites sent
    completedAt: timestamp("completed_at"), // When they reached 3+ invites
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_challenge_invites_user_id").on(table.userId),
    index("idx_challenge_invites_challenge_id").on(table.challengeId),
    unique("unique_user_challenge_invite").on(table.userId, table.challengeId),
  ]
);

// Schema definitions for challenge invites
export const insertChallengeInviteSchema = createInsertSchema(
  challengeInvites
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const selectChallengeInviteSchema = createSelectSchema(challengeInvites);

export type ChallengeInvite = typeof challengeInvites.$inferSelect;
export type InsertChallengeInvite = z.infer<typeof insertChallengeInviteSchema>;

// Schema definitions for friends
export const insertFriendSchema = createInsertSchema(friends, {
  friendEmail: z.string().email().optional(),
  friendPhone: z.string().min(10).max(20).optional(),
  friendName: z.string().min(1).max(100).optional(),
  friendUsername: z.string().min(1).max(50).optional(),
})
  .omit({
    id: true,
    addedAt: true,
  })
  .refine((data) => data.friendEmail || data.friendPhone, {
    message: "Either email or phone number is required",
  });

export const selectFriendSchema = createSelectSchema(friends);

export type Friend = typeof friends.$inferSelect;
export type InsertFriend = z.infer<typeof insertFriendSchema>;

// Schema definitions for SMS configuration
export const insertSmsConfigurationSchema =
  createInsertSchema(smsConfiguration);

export const selectSmsConfigurationSchema =
  createSelectSchema(smsConfiguration);

export type SmsConfiguration = typeof smsConfiguration.$inferSelect;
export type InsertSmsConfiguration = z.infer<
  typeof insertSmsConfigurationSchema
>;

// Schema definitions for SMS templates
export const insertSmsTemplateSchema = createInsertSchema(smsTemplates);

export const selectSmsTemplateSchema = createSelectSchema(smsTemplates);

export type SmsTemplate = typeof smsTemplates.$inferSelect;
export type InsertSmsTemplate = z.infer<typeof insertSmsTemplateSchema>;

// Schema definitions for SMS logs
export const insertSmsLogSchema = createInsertSchema(smsLogs);

export const selectSmsLogSchema = createSelectSchema(smsLogs);

export type SmsLog = typeof smsLogs.$inferSelect;
export type InsertSmsLog = z.infer<typeof insertSogSmsLogSchema>;

// Challenge visibility validation schema
export const updateChallengeVisibilitySchema = z.object({
  isVisible: z.boolean(),
});

export type UpdateChallengeVisibility = z.infer<
  typeof updateChallengeVisibilitySchema
>;
